import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../pages/Interview.css';
import API from '../api/api';
import { isAuthenticated } from '../utils/auth';
import {
    formatToLocalDateTime,
    parseUTCToLocal
} from '../utils/dateUtils';
import {
    ClipboardList, Video, VideoOff, Clock, Calendar,
    CheckCircle, AlertTriangle, Target, Settings, Monitor,
    RefreshCw, ArrowLeft, ArrowRight,
    Check, Eye, Info, MessageSquare
} from './InterviewIcons';
import InterviewAlert from './InterviewAlert';
import DeviceCheckPanel from './DeviceCheckPanel';
import InterviewResume from './InterviewResume';
import InterviewStepper from './InterviewStepper';
import MediaPlaceholder from './MediaPlaceholder';
import { createInterviewProctor } from '../utils/interviewProctor';

// StrictMode double-mounts the init effect in dev, so the first fetch would
// consume the flag and the second would miss it; read it once per page load
// and cache the result (a full reload resets the cache)
const consumedTransitionFlags = new Map();
const consumeTransitionFlag = (key) => {
    if (!consumedTransitionFlags.has(key)) {
        consumedTransitionFlags.set(key, sessionStorage.getItem(key) === '1');
        sessionStorage.removeItem(key);
    }
    return consumedTransitionFlags.get(key);
};

/**
 * Every string this session says that the "Video submissions" rename has to be
 * able to change, with today's exact wording as its default.
 *
 * A key exists here when the default carries a word the rename replaces
 * (`/\b(interview|check-?in|debrief)s?\b/i`) or, in one marked case, a word that
 * would break the workspace's fiction. Everything else the session says —
 * question counts, timers, device copy — reads the same in either world and stays
 * a literal. `src/constants/workspaceLabels.js` holds the override set and
 * `src/tests/workspace/videoSubmissionLabels.test.js` asserts the two stay in step.
 */
export const INTERVIEW_SESSION_LABELS = Object.freeze({
    loading: 'Loading interview...',
    notFound: 'Interview not found',
    accessDenied: 'Access denied to this interview',
    loadFailed: 'Failed to load interview data',
    leaveConfirm: 'Leave the interview? Your in-progress answer will be lost and cannot be recovered, and the question timer will keep running.',
    tagline: 'Complete your interview questions with video responses',
    welcome: (type) => `Welcome to Your ${type}`,
    videoRuleBody: 'Your responses will be recorded with both video and audio for a complete assessment.',
    permissionsBody: "To ensure a smooth interview experience, we need access to your camera and microphone. Please make sure they're properly connected and working.",
    timersBody: 'Closing or reloading this page does not end your interview — you can return and continue from your next unanswered question. But the timer on an open question keeps running while you are away, and an answer you have not submitted is lost. Any question not submitted within its time window is forfeited. Stay on this page until you finish.',
    loggingBody: 'While a question is being recorded, this page logs tab and window focus changes, your overall keyboard and mouse activity levels — never what you type — and camera or microphone changes. These signals are stored with your interview and reviewed as part of interview integrity checks.',
    acceptHint: 'Please accept the conditions above to begin the interview.',
    beginButton: 'Begin Interview',
    practiceIntroBody: "Before we begin the actual interview, let's do a quick practice question to help you get comfortable with the recording process and verify your devices work properly.",
    // the one marked case: "real interview" is the workspace's fiction, not a renamed word
    practiceNoteTail: 'to check how your devices sound and look before starting the real interview.',
    practiceReplayBody: 'Watch your practice recording below to check your video quality, audio clarity, and lighting. Make sure you can see and hear yourself clearly before continuing to the actual interview.',
    practiceTranscriptEmpty: 'Your transcript will show up in the actual interview!',
    practiceContinue: 'Looks Good — Continue to Interview',
    submitTitle: 'Submit your interview',
    submittingTitle: 'Submitting your interview…',
    submitButton: 'Submit Interview',
    submittingAria: 'Submitting your interview',
    submittedHeading: 'Interview submitted',
    endExpired: 'Your session has expired. Your answers are saved — log in again to submit your interview.',
    endFailed: 'Your interview could not be submitted — your answers are saved and nothing is lost. Check your connection and retry.',
    // trailing space is intentional: this is a concat prefix, not a sentence
    mediaRequired: 'Camera and microphone access is required for the interview. ',
    startFailed: 'Failed to start interview. Please try again.',
    startExpired: 'Your session has expired. Log in again to start your interview.'
});

const InterviewSession = ({
    interviewId, 
    workTrialId = null, 
    apiEndpoints, 
    navigationConfig,
    interviewType = "Interview",
    showQuestionPreview = false,
    labels = null
}) => {
    const L = useMemo(() => ({ ...INTERVIEW_SESSION_LABELS, ...(labels || {}) }), [labels]);

    const navigate = useNavigate();

    const navigateTo = (path) => {
        if (path.startsWith('http://') || path.startsWith('https://')) {
            window.location.href = path;
        } else {
            navigate(path);
        }
    };
    
    const videoRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);
    const assemblyAiSocketRef = useRef(null);
    const audioRecorderRef = useRef(null);
    const intervalRef = useRef(null);
    const transcriptAccumulatorRef = useRef('');
    const pendingStreamRef = useRef(null);
    const audioLevelRef = useRef(null);
    const practiceVideoUrlRef = useRef(null);
    // Cached final recording ({ blob, transcript }) so failed submits are retryable
    const finalRecordingRef = useRef(null);
    // Completed transcription turns keyed by "<socketGeneration>:<turn_order>"
    const turnsMapRef = useRef(new Map());
    const partialTurnRef = useRef('');
    const socketGenerationRef = useRef(0);
    const terminationWaitersRef = useRef([]);
    const reconnectAttemptsRef = useRef(0);
    const submitInFlightRef = useRef(false);
    // Single-flight guard for /end (auto-finalize + manual retry can race)
    const endInFlightRef = useRef(false);
    const timerExpiredRef = useRef(false);
    const questionDeadlineRef = useRef(null);
    const mediaFailureRef = useRef(false);
    // Mirrors of state read inside timer/socket callbacks (avoids stale closures)
    const isRecordingRef = useRef(false);
    const isPracticeModeRef = useRef(false);
    const currentQuestionNumberRef = useRef(0);
    const interviewRef = useRef(null);
    const skippedQuestionsRef = useRef([]);
    // Active beforeunload handler so the legit post-submit reload can remove
    // it synchronously (effect cleanup would run too late)
    const unloadGuardRef = useRef(null);
    const showResumeScreenRef = useRef(false);
    const selectedCameraIdRef = useRef('');
    const selectedMicrophoneIdRef = useRef('');
    // groupId|label of the 'default' alias entries; Chrome re-routes the alias
    // live when the OS default changes, and re-enumeration is the only signal
    const defaultDeviceFingerprintsRef = useRef({ audioinput: null, videoinput: null });
    // Proctoring telemetry capture (no-op when apiEndpoints.proctor is absent);
    // fire-and-forget by design — it must never interfere with the interview
    const proctorRef = useRef(null);
    if (proctorRef.current === null) {
        proctorRef.current = createInterviewProctor(apiEndpoints.proctor);
    }

    // sessionStorage flag distinguishing the intentional post-submit reload
    // from an interruption (manual reload / tab close / crash)
    const transitionFlagKey = `interview-transition:${workTrialId || ''}:${interviewId || 'profile'}`;

    // State management
    const [, setUser] = useState(null);
    const [interview, setInterview] = useState(null);
    const [loading, setLoading] = useState(true);
    // Full-page error — reserved for initial load failures only
    const [error, setError] = useState('');
    // Inline in-session alert: { severity: 'warning'|'blocking', id?, message, actionLabel?, onAction? }
    const [sessionAlert, setSessionAlert] = useState(null);
    // Question numbers whose time window expired (410/403 from the server)
    const [skippedQuestions, setSkippedQuestions] = useState([]);
    // Question numbers /end reported as forfeited (server-side truth)
    const [forfeitedQuestions, setForfeitedQuestions] = useState([]);

    // Interruption/resume screen: shown when the interview was already started
    // and the page load was not the intentional post-submit reload
    const [showResumeScreen, setShowResumeScreen] = useState(false);
    // { questionNumber, startedAt: Date|null } for the question being resumed
    const [resumeContext, setResumeContext] = useState(null);
    // Ticks while the resume screen shows a still-running question timer
    const [resumeNow, setResumeNow] = useState(Date.now());

    // Interview state
    const [interviewStarted, setInterviewStarted] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [currentQuestionNumber, setCurrentQuestionNumber] = useState(0);
    const [, setQuestionStartTime] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [transcript, setTranscript] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    // 'idle' | 'active' | 'reconnecting' | 'unavailable'
    const [transcriptionStatus, setTranscriptionStatus] = useState('idle');
    
    // Question preview state (for profile interviews)
    const [previewQuestionData, setPreviewQuestionData] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    
    // Media permissions
    const [mediaPermissions, setMediaPermissions] = useState({
        video: false,
        audio: false,
        granted: false
    });
    
    // Add new state for media testing
    const [mediaTestComplete, setMediaTestComplete] = useState(false);
    const [mediaTestError, setMediaTestError] = useState('');
    
    // Processing states
    const [startingInterview, setStartingInterview] = useState(false);
    const [startingQuestion, setStartingQuestion] = useState(false);
    const [submittingQuestion, setSubmittingQuestion] = useState(false);
    const [endingInterview, setEndingInterview] = useState(false);
    // The automatic finalize attempt failed — surface the button as a retry
    const [endFailed, setEndFailed] = useState(false);
    
    // Practice interview state
    const [showPracticeStep, setShowPracticeStep] = useState(false);
    const [, setPracticeCompleted] = useState(false);
    const [practiceQuestion] = useState({
        text: "Please introduce yourself and tell us why you're interested in this position. This is a practice question to help you get comfortable with the recording process.",
        time_limit: 0.5, // 30 seconds for practice
        assemblyai_key: null // No transcription during practice (set to null to disable)
    });
    const [isPracticeMode, setIsPracticeMode] = useState(false);
    
    // Add successMessage to state
    const [successMessage, setSuccessMessage] = useState('');
    
    // Device selection state
    const [availableCameras, setAvailableCameras] = useState([]);
    const [availableMicrophones, setAvailableMicrophones] = useState([]);
    const [selectedCameraId, setSelectedCameraId] = useState('');
    const [selectedMicrophoneId, setSelectedMicrophoneId] = useState('');
    const [audioLevel, setAudioLevel] = useState(0);
    
    // Practice replay state
    const [practiceVideoUrl, setPracticeVideoUrl] = useState(null);
    const [showPracticeReplay, setShowPracticeReplay] = useState(false);
    
    // Disclaimer state
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    
    // Confirmation page step (separate from setup)
    const [showConfirmationStep, setShowConfirmationStep] = useState(false);

    // Rebuild transcript from the ordered turn map + trailing partial turn.
    // Keying completed turns by turn_order makes the accumulator resilient to
    // AssemblyAI v3 double-emits (formatted turn replaces unformatted).
    const rebuildTranscriptFromTurns = () => {
        const completed = Array.from(turnsMapRef.current.values()).join(' ').trim();
        const partial = partialTurnRef.current.trim();
        const full = partial ? (completed ? completed + ' ' + partial : partial) : completed;
        transcriptAccumulatorRef.current = full;
        setTranscript(full);
    };

    const flushTerminationWaiters = () => {
        const waiters = terminationWaitersRef.current;
        terminationWaitersRef.current = [];
        waiters.forEach((resolve) => resolve());
    };

    // Bounded wait for the socket's final turn/termination messages after Terminate
    const waitForTranscriptFinal = (timeoutMs = 2000) => {
        const socket = assemblyAiSocketRef.current;
        if (!socket || socket.readyState !== WebSocket.OPEN) return Promise.resolve();
        return new Promise((resolve) => {
            const timer = setTimeout(resolve, timeoutMs);
            terminationWaitersRef.current.push(() => {
                clearTimeout(timer);
                resolve();
            });
        });
    };

    // Socket died mid-recording: recovery start_question returns a fresh
    // assemblyai_key without resetting server-side state. Max 2 attempts,
    // then degrade gracefully — recording is unaffected either way.
    const attemptTranscriptionReconnect = async () => {
        if (!isRecordingRef.current || isPracticeModeRef.current) return;
        if (reconnectAttemptsRef.current >= 2) {
            setTranscriptionStatus('unavailable');
            return;
        }
        reconnectAttemptsRef.current += 1;
        setTranscriptionStatus('reconnecting');
        try {
            const response = await API.post(
                apiEndpoints.startQuestion.replace(':questionNumber', currentQuestionNumberRef.current),
                {},
                { skipAuthRedirect: true }
            );
            const freshKey = response.data?.question?.assemblyai_key;
            if (!isRecordingRef.current) return;
            if (!freshKey) throw new Error('No transcription key in recovery response');
            setupAssemblyAITranscription(freshKey);
        } catch (error) {
            console.error('[AssemblyAI] Reconnect attempt failed:', error);
            if (reconnectAttemptsRef.current >= 2) {
                setTranscriptionStatus('unavailable');
            } else {
                attemptTranscriptionReconnect();
            }
        }
    };

    // AssemblyAI transcription setup (Universal Streaming)
    const setupAssemblyAITranscription = (token, onConnectedCallback = null) => {
        if (!token) {
            console.warn('[AssemblyAI] No token provided');
            return null;
        }

        try {
            // Close existing connection if any
            if (assemblyAiSocketRef.current) {
                assemblyAiSocketRef.current.close();
                assemblyAiSocketRef.current = null;
            }

            // Validate token format
            if (!token || typeof token !== 'string' || token.length < 10) {
                console.error('[AssemblyAI] Invalid token format');
                return null;
            }

            // v3 Universal Streaming: pass config as URL query params
            const wsUrl = `wss://streaming.assemblyai.com/v3/ws?sample_rate=16000&format_turns=true&token=${encodeURIComponent(token)}`;
            const socket = new WebSocket(wsUrl);

            // Reconnected sessions restart turn_order at 0, so completed turns
            // are keyed per socket generation to avoid cross-session collisions
            const generation = ++socketGenerationRef.current;
            let fallbackTurnCounter = 0;

            // Set a connection timeout
            const connectionTimeout = setTimeout(() => {
                if (socket.readyState === WebSocket.CONNECTING) {
                    console.error('[AssemblyAI] Connection timeout');
                    socket.close();
                }
            }, 10000);

            socket.onopen = () => {
                console.log('✅ [AssemblyAI] Connected');
                clearTimeout(connectionTimeout);
                setTranscriptionStatus('active');

                if (onConnectedCallback && typeof onConnectedCallback === 'function') {
                    onConnectedCallback().catch(error => {
                        console.error('[AssemblyAI] Callback error:', error);
                    });
                }
            };

            socket.onmessage = (event) => {
                try {
                    const response = JSON.parse(event.data);
                    const msgType = response.type;

                    if (msgType === 'Begin') {
                        console.log('✅ [AssemblyAI] Session started, id:', response.id);

                    } else if (msgType === 'Turn') {
                        // v3 Turn messages carry a cumulative `transcript` for the
                        // current turn. `end_of_turn` marks completion; the formatted
                        // version re-emits with the same turn_order and replaces it.
                        const turnText = (response.transcript || '').trim();

                        if (response.end_of_turn) {
                            if (turnText) {
                                const orderKey = (response.turn_order !== undefined && response.turn_order !== null)
                                    ? response.turn_order
                                    : `u${fallbackTurnCounter++}`;
                                turnsMapRef.current.set(`${generation}:${orderKey}`, turnText);
                            }
                            partialTurnRef.current = '';
                        } else if (turnText) {
                            partialTurnRef.current = turnText;
                        }
                        rebuildTranscriptFromTurns();

                    } else if (msgType === 'Termination') {
                        console.log('[AssemblyAI] Session terminated:',
                            `audio=${response.audio_duration_seconds}s,`,
                            `session=${response.session_duration_seconds}s`);
                        flushTerminationWaiters();
                    } else {
                        console.log('⚠️ [AssemblyAI] Unknown message type:', msgType);
                    }

                } catch (error) {
                    console.error('[AssemblyAI] Parse error:', error);
                }
            };

            socket.onerror = (error) => {
                console.error('[AssemblyAI] WebSocket error:', error);
                clearTimeout(connectionTimeout);
            };

            socket.onclose = (event) => {
                clearTimeout(connectionTimeout);
                // A replaced socket's async close must not clobber the ref
                // when it already points at a newer socket. Intentional closes
                // (stopRecording/stopCamera) null the ref first, so an unexpected
                // close while recording is the only path into reconnection.
                if (assemblyAiSocketRef.current === socket) {
                    assemblyAiSocketRef.current = null;
                    flushTerminationWaiters();
                    // The dead socket can no longer finalize its in-progress turn;
                    // promote the partial under a synthetic key for this generation
                    // so the next generation's first partial doesn't erase it
                    const orphanedPartial = partialTurnRef.current.trim();
                    if (orphanedPartial) {
                        turnsMapRef.current.set(`${generation}:partial`, orphanedPartial);
                        partialTurnRef.current = '';
                        rebuildTranscriptFromTurns();
                    }
                    if (isRecordingRef.current && !isPracticeModeRef.current) {
                        attemptTranscriptionReconnect();
                    } else {
                        setTranscriptionStatus('idle');
                    }
                }

                // Only log errors (not normal closures)
                if (event.code !== 1000) {
                    const errorMessages = {
                        // Standard WebSocket codes
                        1006: 'Connection closed abnormally',
                        // v3 Universal Streaming close codes (3000-range)
                        3000: 'Session not found or already closed',
                        3001: 'Server error',
                        3002: 'Rate limited',
                        3003: 'Session timeout - no audio received',
                        3004: 'Session expired - max duration exceeded',
                        3005: 'Session closed - invalid or expired token',
                        3006: 'Bad request - invalid message format',
                        // Legacy v2 close codes (4000-range)
                        4001: 'Not authorized - invalid token',
                        4002: 'Insufficient funds',
                        4003: 'Requires paid plan',
                        4008: 'Session expired',
                        4029: 'Rate limited',
                        4031: 'Session timeout - no audio received'
                    };
                    const message = errorMessages[event.code] || `Code: ${event.code}`;
                    console.error(`[AssemblyAI] Closed: ${message}`);
                }
            };
            
            assemblyAiSocketRef.current = socket;
            return socket;
            
        } catch (error) {
            console.error('[AssemblyAI] Setup error:', error);
            return null;
        }
    };

    // Initialize page
    useEffect(() => {
        if (!isAuthenticated()) {
            navigate("/login");
            return;
        }

        // Get user data
        API.get("users/account-data").then((response) => {
            setUser(response.data.user);
            fetchInterviewData();
        }).catch((err) => {
            console.error("Error fetching user data:", err);
            navigate("/login");
        });

        // Initialize transcript storage
        transcriptAccumulatorRef.current = '';

        // Final-flush telemetry on tab close/reload (pagehide beacon)
        proctorRef.current.attachLifecycle();

        const onDeviceChange = () => {
            navigator.mediaDevices.enumerateDevices()
                .then((devices) => trackDefaultDeviceFingerprints(devices, true))
                .catch(() => {});
        };
        if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
            navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
        }

        return () => {
            // Cleanup - stop camera and all media streams when component unmounts
            console.log('🧹 Component unmounting, cleaning up media streams...');
            if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
                navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
            }
            proctorRef.current.teardown();
            stopCamera();
            stopAudioLevelMonitoring();
            
            // Clean up practice video URL
            if (practiceVideoUrlRef.current) {
                URL.revokeObjectURL(practiceVideoUrlRef.current);
                practiceVideoUrlRef.current = null;
            }
            
            // Clear intervals
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interviewId, workTrialId, navigate]);

    // Keep refs in sync with state read from timer/socket callbacks
    useEffect(() => { isPracticeModeRef.current = isPracticeMode; }, [isPracticeMode]);
    useEffect(() => { currentQuestionNumberRef.current = currentQuestionNumber; }, [currentQuestionNumber]);
    useEffect(() => { interviewRef.current = interview; }, [interview]);
    useEffect(() => { skippedQuestionsRef.current = skippedQuestions; }, [skippedQuestions]);
    useEffect(() => { showResumeScreenRef.current = showResumeScreen; }, [showResumeScreen]);
    useEffect(() => { selectedCameraIdRef.current = selectedCameraId; }, [selectedCameraId]);
    useEffect(() => { selectedMicrophoneIdRef.current = selectedMicrophoneId; }, [selectedMicrophoneId]);

    // Browser confirmation while an answer is live (recording or waiting to
    // submit) — not during practice, between questions, or after completion.
    // The intentional post-submit reload releases the guard first.
    const questionEngaged = interviewStarted && !!currentQuestion && !isPracticeMode && !successMessage;
    useEffect(() => {
        if (!questionEngaged) return;
        const handler = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        unloadGuardRef.current = handler;
        window.addEventListener('beforeunload', handler);
        return () => {
            window.removeEventListener('beforeunload', handler);
            if (unloadGuardRef.current === handler) unloadGuardRef.current = null;
        };
    }, [questionEngaged]);

    const releaseUnloadGuard = () => {
        if (unloadGuardRef.current) {
            window.removeEventListener('beforeunload', unloadGuardRef.current);
            unloadGuardRef.current = null;
        }
    };

    // Live elapsed counter on the resume screen while the question timer runs
    useEffect(() => {
        if (!showResumeScreen || !resumeContext?.startedAt) return;
        const id = setInterval(() => setResumeNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [showResumeScreen, resumeContext]);

    // Leaving the resume screen swaps video elements; hand the checked stream
    // to the newly mounted element so the preview stays live
    useEffect(() => {
        if (!showResumeScreen && interviewStarted &&
            pendingStreamRef.current && videoRef.current && !videoRef.current.srcObject) {
            videoRef.current.srcObject = pendingStreamRef.current;
            pendingStreamRef.current = null;
            videoRef.current.play().catch(() => {});
        }
    }, [showResumeScreen, interviewStarted]);

    // Set up video stream when media test completes and video element is available
    useEffect(() => {
        if (mediaTestComplete && videoRef.current) {
            // If there's a pending stream, set it now
            if (pendingStreamRef.current) {
                console.log('🎥 Setting pending stream to video element...');
                videoRef.current.srcObject = pendingStreamRef.current;
                pendingStreamRef.current = null; // Clear the pending stream
            }
            
            // Ensure the stream is set and try to play
            if (videoRef.current.srcObject) {
                const playVideo = async () => {
                    if (videoRef.current && videoRef.current.srcObject) {
                        try {
                            await videoRef.current.play();
                            console.log('✅ Video playing after media test completion');
                        } catch (playError) {
                            console.log('⚠️ Video autoplay prevented, but stream is set');
                        }
                    }
                };
                
                // Small delay to ensure element is ready and visible
                setTimeout(playVideo, 200);
            }
        }
    }, [mediaTestComplete]);

    // Stop camera when interview ends or success message is shown
    useEffect(() => {
        if (successMessage) {
            console.log('✅ Interview completed, stopping camera...');
            stopCamera();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [successMessage]);

    // Ensure video stream is active when interview starts
    useEffect(() => {
        // On the resume screen the device check drives acquisition; at the
        // completion stage (no question left) the camera is not needed
        if (showResumeScreenRef.current || !currentQuestionNumberRef.current) return;
        if (interviewStarted) {
            console.log('🎥 Interview started useEffect triggered, getting video stream...');
            
            // Get video stream immediately when interview starts
            const setupVideoStream = async () => {
                try {
                    // Use existing stream if available, or request new one
                    let stream = videoRef.current?.srcObject;
                    
                    if (!stream || !stream.active) {
                        console.log('🔄 No active stream, requesting new one with selected devices...');
                        stream = await navigator.mediaDevices.getUserMedia(getMediaConstraints());
                    }
                    
                    if (stream && videoRef.current) {
                        console.log('🎥 Setting stream to video element in useEffect...');
                        videoRef.current.srcObject = stream;
                        
                        // Ensure video plays
                        try {
                            await videoRef.current.play();
                            console.log('✅ Video playing successfully in useEffect');
                        } catch (playError) {
                            console.log('⚠️ Video autoplay prevented in useEffect, setting up click handler');
                            // Add click handler to play video
                            const playVideo = () => {
                                if (videoRef.current) {
                                    videoRef.current.play().then(() => {
                                        console.log('✅ Video started playing after user interaction');
                                        document.removeEventListener('click', playVideo);
                                    }).catch(() => {});
                                }
                            };
                            document.addEventListener('click', playVideo, { once: true });
                        }
                    }
                } catch (error) {
                    console.error('❌ Failed to setup video stream in useEffect:', error);
                }
            };
            
            setupVideoStream();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interviewStarted]);

    const fetchInterviewData = async () => {
        try {
            setLoading(true);
            
            const response = await API.get(apiEndpoints.fetch);
            
            if (response.data && response.data.interview) {
                const interviewData = response.data.interview;
                setInterview(interviewData);
                
                // Check if interview has already been started
                if (interviewData.start_date) {
                    setInterviewStarted(true);

                    // Intentional post-submit reloads set this flag just before
                    // reloading; anything else (manual reload, tab close, crash,
                    // returning later) is an interruption
                    const cleanTransition = consumeTransitionFlag(transitionFlagKey);

                    if (interviewData.question_numbers && interviewData.question_numbers.length > 0) {
                        const hasStartedQuestions = interviewData.question_start_dates &&
                            Object.keys(interviewData.question_start_dates).length > 0;

                        const nextQuestion = hasStartedQuestions
                            ? findNextQuestion(interviewData)
                            : interviewData.question_numbers[0];

                        if (nextQuestion) {
                            setCurrentQuestionNumber(nextQuestion);

                            if (!cleanTransition) {
                                const startRaw = interviewData.question_start_dates &&
                                    interviewData.question_start_dates[String(nextQuestion)];
                                setResumeContext({
                                    questionNumber: nextQuestion,
                                    startedAt: startRaw ? parseUTCToLocal(startRaw) : null
                                });
                                showResumeScreenRef.current = true;
                                setShowResumeScreen(true);
                                proctorRef.current.reloadDetected(nextQuestion);
                            }
                        }
                    }
                }
            }
            
        } catch (error) {
            console.error("Error fetching interview data:", error);
            const serverMessage = error.response?.data?.error;
            if (serverMessage) {
                setError(serverMessage);
            } else if (error.response?.status === 404) {
                setError(L.notFound);
            } else if (error.response?.status === 403) {
                setError(L.accessDenied);
            } else {
                setError(L.loadFailed);
            }
        } finally {
            setLoading(false);
        }
    };

    const findNextQuestion = (interviewData) => {
        if (!interviewData || !interviewData.question_numbers) return null;

        // First question that is neither submitted nor skipped (expired questions
        // never get a question_submission_dates entry, so without the skip list
        // the flow would loop on them forever)
        for (const questionNum of [...interviewData.question_numbers].sort((a, b) => a - b)) {
            const questionKey = questionNum.toString();
            const submitted = interviewData.question_submission_dates &&
                interviewData.question_submission_dates[questionKey];
            if (!submitted && !skippedQuestionsRef.current.includes(questionNum)) {
                return questionNum;
            }
        }

        return null; // All questions completed
    };

    // Advance past a question whose time window expired (410 time_expired on
    // start, or 403 time_limit_exceeded on submit). /end forfeits these server-side.
    const skipCurrentQuestion = () => {
        proctorRef.current.stopCapture();
        const questionNumber = currentQuestionNumberRef.current;
        const updatedSkipped = skippedQuestionsRef.current.includes(questionNumber)
            ? skippedQuestionsRef.current
            : [...skippedQuestionsRef.current, questionNumber];
        skippedQuestionsRef.current = updatedSkipped;
        setSkippedQuestions(updatedSkipped);

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        questionDeadlineRef.current = null;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            stopRecording().catch(() => {});
        }

        setSessionAlert(null);
        setSubmittingQuestion(false);
        setCurrentQuestion(null);
        setQuestionStartTime(null);
        setTimeRemaining(0);
        setTranscript('');
        transcriptAccumulatorRef.current = '';
        turnsMapRef.current = new Map();
        partialTurnRef.current = '';
        finalRecordingRef.current = null;
        recordedChunksRef.current = [];
        setTranscriptionStatus('idle');

        const next = findNextQuestion(interviewRef.current);
        // Completion stage unmounts the video column — release the camera
        // now, while the video element is still reachable
        if (!next) stopCamera();
        setCurrentQuestionNumber(next || 0);
    };

    const testMediaDevices = async () => {
        setMediaTestError('');
        try {
            // Clean up any existing stream first
            if (videoRef.current && videoRef.current.srcObject) {
                const existingStream = videoRef.current.srcObject;
                const tracks = existingStream.getTracks();
                tracks.forEach(track => track.stop());
                videoRef.current.srcObject = null;
            }

            // First check if devices are available
            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasVideo = devices.some(device => device.kind === 'videoinput');
            const hasAudio = devices.some(device => device.kind === 'audioinput');

            if (!hasVideo) {
                throw new Error('No camera device found');
            }
            if (!hasAudio) {
                throw new Error('No microphone device found');
            }

            // Test and keep the video stream active for preview
            // Use selected devices if already chosen (e.g., when re-requesting after page transition)
            const stream = await navigator.mediaDevices.getUserMedia({ 
                video: {
                    ...(selectedCameraId ? { deviceId: { exact: selectedCameraId } } : {}),
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'user'
                },
                audio: {
                    ...(selectedMicrophoneId ? { deviceId: { exact: selectedMicrophoneId } } : {}),
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 16000
                }
            });

            // Store the stream for later use (will be set to video element in useEffect)
            pendingStreamRef.current = stream;
            
            // Try to set it immediately if the element is available
            if (videoRef.current) {
                console.log('🎥 Setting stream to video element after media test...');
                videoRef.current.srcObject = stream;
                
                // Force the video to load and play
                try {
                    await videoRef.current.play();
                    console.log('✅ Camera preview activated and playing after media test');
                    pendingStreamRef.current = null; // Clear pending since we set it directly
                } catch (playError) {
                    console.log('⚠️ Video autoplay prevented, but stream is set. Error:', playError);
                    // Keep pendingStreamRef so useEffect can handle it
                    // Try again after a short delay
                    setTimeout(async () => {
                        if (videoRef.current) {
                            try {
                                await videoRef.current.play();
                                console.log('✅ Video playing after retry');
                                pendingStreamRef.current = null;
                            } catch (retryError) {
                                console.log('❌ Video play retry failed:', retryError);
                            }
                        }
                    }, 500);
                }
            } else {
                console.log('⏳ Video element not yet rendered, stream will be set when element is available');
                // The stream is stored in pendingStreamRef and will be set in the useEffect
            }

            setMediaPermissions({
                video: true,
                audio: true,
                granted: true
            });

            // Enumerate devices (labels are available after permission is granted)
            await enumerateDevices(stream);
            
            // Start audio level monitoring
            startAudioLevelMonitoring(stream);

            // If we get here, both devices are working
            setMediaTestComplete(true);
            return stream;
        } catch (error) {
            console.error('Media test error:', error);
            setMediaTestError(error.message);
            setMediaTestComplete(false);
            return null;
        }
    };

    // Enumerate available cameras and microphones
    const enumerateDevices = async (currentStream = null) => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const cameras = devices.filter(d => d.kind === 'videoinput');
            const mics = devices.filter(d => d.kind === 'audioinput');
            setAvailableCameras(cameras);
            setAvailableMicrophones(mics);
            trackDefaultDeviceFingerprints(devices, false);

            // Chrome exposes the system default as deviceId 'default'; prefer it
            // for the initial selection (cameras rarely have one — first wins)
            const defaultIdOf = (list) => {
                const systemDefault = list.find(d => d.deviceId === 'default');
                return systemDefault ? systemDefault.deviceId : (list[0] ? list[0].deviceId : '');
            };

            // Auto-select current devices from the active stream. With no prior
            // explicit selection the stream was opened unconstrained (browser
            // default), so the 'default' entry matches the live track — keep the
            // selection on it so later constraints keep using the system default.
            if (currentStream) {
                const videoTrack = currentStream.getVideoTracks()[0];
                const audioTrack = currentStream.getAudioTracks()[0];
                if (videoTrack) {
                    const settings = videoTrack.getSettings();
                    if (!selectedCameraId && cameras.some(d => d.deviceId === 'default')) {
                        setSelectedCameraId('default');
                    } else if (selectedCameraId !== 'default' && settings.deviceId) {
                        // Track settings echo a concrete id; an explicit
                        // 'default' selection must stay on the default alias
                        setSelectedCameraId(settings.deviceId);
                    }
                }
                if (audioTrack) {
                    const settings = audioTrack.getSettings();
                    if (!selectedMicrophoneId && mics.some(d => d.deviceId === 'default')) {
                        setSelectedMicrophoneId('default');
                    } else if (selectedMicrophoneId !== 'default' && settings.deviceId) {
                        setSelectedMicrophoneId(settings.deviceId);
                    }
                }
            } else {
                if (cameras.length > 0) setSelectedCameraId(defaultIdOf(cameras));
                if (mics.length > 0) setSelectedMicrophoneId(defaultIdOf(mics));
            }
        } catch (error) {
            console.error('Error enumerating devices:', error);
        }
    };

    // Compare/record the 'default' alias entries so a silent OS-level default
    // switch during capture still produces telemetry
    const trackDefaultDeviceFingerprints = (devices, emitOnChange) => {
        const fingerprints = defaultDeviceFingerprintsRef.current;
        for (const kind of ['audioinput', 'videoinput']) {
            const entry = devices.find(d => d.kind === kind && d.deviceId === 'default');
            const fingerprint = entry ? `${entry.groupId}|${entry.label}` : null;
            const selectedId = kind === 'audioinput' ? selectedMicrophoneIdRef.current : selectedCameraIdRef.current;
            if (emitOnChange && fingerprints[kind] && fingerprint &&
                fingerprint !== fingerprints[kind] && selectedId === 'default') {
                // 'device-changed' is the whitelisted action; os-default suffix marks
                // an OS-level default reroute rather than an in-app switch
                proctorRef.current.mediaTrackEvent('device-changed', kind === 'audioinput' ? 'audio-os-default' : 'video-os-default');
            }
            fingerprints[kind] = fingerprint;
        }
    };

    // Audio level monitoring for mic test
    const startAudioLevelMonitoring = (stream) => {
        try {
            stopAudioLevelMonitoring();
            
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.8;
            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);
            
            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            let lastUpdate = 0;
            
            const updateLevel = (timestamp) => {
                if (!audioLevelRef.current) return;
                
                // Throttle updates to ~15fps for performance
                if (timestamp - lastUpdate < 66) {
                    audioLevelRef.current.animationFrame = requestAnimationFrame(updateLevel);
                    return;
                }
                lastUpdate = timestamp;
                
                analyser.getByteTimeDomainData(dataArray);
                
                let sumSquares = 0;
                for (let i = 0; i < dataArray.length; i++) {
                    const normalized = (dataArray[i] - 128) / 128;
                    sumSquares += normalized * normalized;
                }
                const rms = Math.sqrt(sumSquares / dataArray.length);
                const level = Math.min(100, Math.round(rms * 350));
                
                setAudioLevel(level);
                audioLevelRef.current.animationFrame = requestAnimationFrame(updateLevel);
            };
            
            audioLevelRef.current = { analyser, audioContext, source, animationFrame: null };
            audioLevelRef.current.animationFrame = requestAnimationFrame(updateLevel);
        } catch (error) {
            console.error('Error starting audio level monitoring:', error);
        }
    };

    const stopAudioLevelMonitoring = () => {
        if (audioLevelRef.current) {
            if (audioLevelRef.current.animationFrame) {
                cancelAnimationFrame(audioLevelRef.current.animationFrame);
            }
            if (audioLevelRef.current.source) {
                try { audioLevelRef.current.source.disconnect(); } catch (e) { /* ignore */ }
            }
            if (audioLevelRef.current.audioContext && audioLevelRef.current.audioContext.state !== 'closed') {
                try { audioLevelRef.current.audioContext.close(); } catch (e) { /* ignore */ }
            }
            audioLevelRef.current = null;
        }
        setAudioLevel(0);
    };

    // Switch to a different camera or microphone
    const switchDevice = async (cameraId, micId) => {
        try {
            stopAudioLevelMonitoring();
            
            // Stop current stream tracks
            if (videoRef.current && videoRef.current.srcObject) {
                videoRef.current.srcObject.getTracks().forEach(track => track.stop());
                videoRef.current.srcObject = null;
            }
            
            const constraints = {
                video: {
                    ...(cameraId ? { deviceId: { exact: cameraId } } : {}),
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'user'
                },
                audio: {
                    ...(micId ? { deviceId: { exact: micId } } : {}),
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 16000
                }
            };
            
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            pendingStreamRef.current = stream;
            
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                try {
                    await videoRef.current.play();
                    pendingStreamRef.current = null;
                } catch (e) {
                    console.log('Auto-play prevented on device switch');
                }
            }
            
            startAudioLevelMonitoring(stream);
            return stream;
        } catch (error) {
            console.error('Error switching device:', error);
            setMediaTestError('Failed to switch device: ' + error.message);
            return null;
        }
    };

    // device-changed telemetry only for a real switch: a previous selection
    // must exist and differ (initial population/auto-select emits nothing)
    const handleCameraChange = async (cameraId) => {
        if (selectedCameraId && cameraId !== selectedCameraId) {
            proctorRef.current.deviceChanged('camera');
        }
        setSelectedCameraId(cameraId);
        await switchDevice(cameraId, selectedMicrophoneId);
    };

    const handleMicrophoneChange = async (microphoneId) => {
        if (selectedMicrophoneId && microphoneId !== selectedMicrophoneId) {
            proctorRef.current.deviceChanged('microphone');
        }
        setSelectedMicrophoneId(microphoneId);
        await switchDevice(selectedCameraId, microphoneId);
    };

    // 1-based display position of a question number within the interview
    // (ref for stale-closure-safe event handlers, state for first render)
    const questionPositionOf = (questionNumber) => {
        const data = interviewRef.current || interview;
        const idx = data?.question_numbers?.indexOf(questionNumber);
        return (idx !== undefined && idx >= 0) ? idx + 1 : questionNumber;
    };

    const handleResumeContinue = async () => {
        const stream = videoRef.current?.srcObject;
        if (stream && stream.active) pendingStreamRef.current = stream;
        showResumeScreenRef.current = false;
        setShowResumeScreen(false);
        // A question whose timer is already running goes straight back in
        // (recovery start returns the true remaining time); an unstarted
        // question gets the normal waiting screen so its timer stays untouched
        if (resumeContext?.startedAt) {
            await handleStartQuestion(resumeContext.questionNumber);
        }
    };

    // Build getUserMedia constraints using the user's selected devices
    const getMediaConstraints = () => {
        return {
            video: {
                ...(selectedCameraId ? { deviceId: { exact: selectedCameraId } } : {}),
                width: { ideal: 1280 },
                height: { ideal: 720 },
                facingMode: 'user'
            },
            audio: {
                ...(selectedMicrophoneId ? { deviceId: { exact: selectedMicrophoneId } } : {}),
                echoCancellation: true,
                noiseSuppression: true,
                sampleRate: 16000
            }
        };
    };

    const ensureVideoStream = async () => {
        console.log('🔍 Checking video stream status...');
        
        // Check if video stream is already active
        if (videoRef.current && videoRef.current.srcObject) {
            const tracks = videoRef.current.srcObject.getTracks();
            console.log('📊 Current stream tracks:', tracks.length);
            if (tracks.length > 0) {
                console.log('📊 Track readyState:', tracks[0].readyState);
                if (tracks[0].readyState === 'live') {
                    console.log('✅ Existing stream is active, reusing it');
                    return videoRef.current.srcObject;
                }
            }
        }
        
        console.log('🔄 Requesting new video stream...');
        // If not active, request new stream
        const stream = await requestMediaPermissions();
        
        // Ensure the video element gets the stream
        if (stream && videoRef.current) {
            console.log('🎥 Setting new stream to video element');
            videoRef.current.srcObject = stream;
            try {
                await videoRef.current.play();
                console.log('✅ Video stream activated and playing');
            } catch (playError) {
                console.log('⚠️ Video autoplay prevented, but stream is set');
            }
        }
        
        return stream;
    };

    const requestMediaPermissions = async () => {
        try {
            // If media test is already complete, reuse the existing stream
            if (mediaTestComplete && videoRef.current && videoRef.current.srcObject) {
                const existingStream = videoRef.current.srcObject;
                const tracks = existingStream.getTracks();
                if (tracks.length > 0 && tracks[0].readyState === 'live') {
                    console.log('✅ Reusing existing media stream');
                    return existingStream;
                }
            }

            // Otherwise, test the devices (which will request a new stream)
            const stream = await testMediaDevices();
            if (!stream) {
                throw new Error(mediaTestError || 'Media devices test failed');
            }
            
            // Stream is already set in testMediaDevices, just ensure it's playing
            if (videoRef.current && videoRef.current.srcObject) {
                try {
                    await videoRef.current.play();
                } catch (playError) {
                    console.log('Video autoplay prevented, but stream is set');
                }
            }
            
            return stream;
        } catch (error) {
            console.error('Error accessing media devices:', error);
            let errorMessage = L.mediaRequired;
            
            if (error.name === 'NotAllowedError') {
                errorMessage += 'Please allow permissions in your browser settings and try again.';
            } else if (error.name === 'NotFoundError') {
                errorMessage += 'No camera or microphone found. Please connect the required devices.';
            } else if (error.name === 'NotReadableError') {
                errorMessage += 'Your camera or microphone is already in use by another application.';
            } else {
                errorMessage += error.message || 'Please check your devices and try again.';
            }

            setSessionAlert({ severity: 'blocking', message: errorMessage });
            return null;
        }
    };

    // Setup page → Confirmation page
    const handleProceedToConfirmation = () => {
        setShowConfirmationStep(true);
    };

    // Confirmation page → Practice step
    const handleStartInterview = async () => {
        setStartingInterview(true);
        setSessionAlert(null);

        try {
            // Ensure media stream is active (should already be from media test)
            let stream = videoRef.current?.srcObject;
            if (!stream || !stream.active) {
                stream = await requestMediaPermissions();
                if (!stream) {
                    setStartingInterview(false);
                    return;
                }
            }
            
            // Ensure video preview is working
            if (videoRef.current) {
                if (!videoRef.current.srcObject) {
                    videoRef.current.srcObject = stream;
                }
                // Make sure video is playing
                try {
                    await videoRef.current.play();
                } catch (playError) {
                    console.log('Video autoplay prevented, but stream is set');
                }
            }
            
            // Hide confirmation, show practice step
            setShowConfirmationStep(false);
            setShowPracticeStep(true);
            
        } catch (error) {
            console.error("Error starting interview:", error);
            setSessionAlert({ severity: 'blocking', message: L.startFailed });
        } finally {
            setStartingInterview(false);
        }
    };

    const handleStartPractice = async () => {
        setIsPracticeMode(true);
        isPracticeModeRef.current = true;
        setCurrentQuestion(practiceQuestion);
        setQuestionStartTime(new Date());
        setTimeRemaining(Math.round(practiceQuestion.time_limit * 60));
        setTranscript('');
        setSessionAlert(null);

        // Clear transcript accumulator for practice question
        transcriptAccumulatorRef.current = '';
        turnsMapRef.current = new Map();
        partialTurnRef.current = '';
        finalRecordingRef.current = null;
        recordedChunksRef.current = [];

        // Start timer
        startQuestionTimer(Math.round(practiceQuestion.time_limit * 60));
        
        // Start recording for practice with transcription enabled
        await startRecording(practiceQuestion.assemblyai_key);
    };

    const handleCompletePractice = async () => {
        // Stop recording and timer
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        
        const practiceBlob = await stopRecording();
        
        // Create replay URL so user can review their practice recording
        if (practiceBlob && practiceBlob.size > 0) {
            // Revoke old URL if exists
            if (practiceVideoUrlRef.current) {
                URL.revokeObjectURL(practiceVideoUrlRef.current);
            }
            const url = URL.createObjectURL(practiceBlob);
            practiceVideoUrlRef.current = url;
            setPracticeVideoUrl(url);
            setShowPracticeReplay(true);
        }
        
        setIsPracticeMode(false);
        isPracticeModeRef.current = false;
        setCurrentQuestion(null);
        setPracticeCompleted(true);
        // Don't hide practice step yet — show replay first
        // Don't start actual interview yet — user must review practice first
    };

    const handleContinueAfterPractice = async () => {
        // Clean up practice video URL
        if (practiceVideoUrlRef.current) {
            URL.revokeObjectURL(practiceVideoUrlRef.current);
            practiceVideoUrlRef.current = null;
        }
        setPracticeVideoUrl(null);
        setShowPracticeReplay(false);
        setShowPracticeStep(false);
        
        // Ensure video stream is maintained
        await ensureVideoStream();
        
        // Now start the actual interview
        await startActualInterview();
    };

    const handleRedoPractice = async () => {
        // Clean up practice video URL
        if (practiceVideoUrlRef.current) {
            URL.revokeObjectURL(practiceVideoUrlRef.current);
            practiceVideoUrlRef.current = null;
        }
        setPracticeVideoUrl(null);
        setShowPracticeReplay(false);
        setPracticeCompleted(false);
        
        // Re-establish video stream for camera preview
        await ensureVideoStream();
    };

    const startActualInterview = async () => {
        try {
            console.log('🎬 Starting actual interview...');

            const response = await API.post(apiEndpoints.start, {}, { skipAuthRedirect: true });
            
            if (response.data) {
                console.log('✅ Interview started successfully');
                setInterviewStarted(true);
                proctorRef.current.interviewStarted();
                
                // Start with the first question if available
                if (interview.question_numbers && interview.question_numbers.length > 0) {
                    setCurrentQuestionNumber(interview.question_numbers[0]);
                    console.log('🎯 Set current question number:', interview.question_numbers[0]);
                }
                
                // IMMEDIATELY get video stream and show it
                console.log('🎥 Getting video stream for preview...');
                const stream = await ensureVideoStream();
                if (stream && videoRef.current) {
                    console.log('🎥 Setting video stream to video element...');
                    videoRef.current.srcObject = stream;
                    
                    // Force play the video
                    try {
                        await videoRef.current.play();
                        console.log('✅ Video is now playing and visible');
                    } catch (playError) {
                        console.log('⚠️ Video autoplay prevented, but stream is set');
                        // Try to play again after a short delay
                        setTimeout(() => {
                            if (videoRef.current) {
                                videoRef.current.play().catch(e => console.log('Second play attempt failed'));
                            }
                        }, 100);
                    }
                } else {
                    console.error('❌ Failed to get video stream for preview');
                }
            }
            
        } catch (error) {
            console.error("Error starting interview:", error);
            const status = error.response?.status;
            const errorCode = error.response?.data?.error_code;
            if (status === 401) {
                setSessionAlert({
                    severity: 'blocking',
                    message: L.startExpired,
                    actionLabel: 'Go to Login',
                    onAction: () => { stopCamera(); navigateTo('/login'); }
                });
            } else if (status === 403 && errorCode === 'video_window_closed') {
                /* The window is shut for good: no later attempt can open it, so the
                   alert carries no action. A Retry here would be a button that
                   cannot work. */
                setSessionAlert({
                    severity: 'blocking',
                    message: 'The video submission window for this work trial has closed, so no recording can be made. The window cannot reopen.'
                });
            } else {
                setSessionAlert({
                    severity: 'blocking',
                    message: error.response?.data?.error || L.startFailed,
                    actionLabel: 'Retry',
                    onAction: startActualInterview
                });
            }
        }
    };

    const fetchQuestionPreview = async (questionNumber) => {
        if (!showQuestionPreview || !apiEndpoints.previewQuestion) return;
        
        setLoadingPreview(true);
        setPreviewQuestionData(null);
        
        try {
            const response = await API.get(
                apiEndpoints.previewQuestion.replace(':questionNumber', questionNumber),
                { skipAuthRedirect: true }
            );
            if (response.data && response.data.question) {
                setPreviewQuestionData(response.data.question);
            }
        } catch (error) {
            console.error("Error fetching question preview:", error);
            if (error.response?.status === 401) {
                setSessionAlert({
                    severity: 'blocking',
                    message: 'Your session has expired. Your submitted answers are saved — log in again and you will continue from your next unanswered question.',
                    actionLabel: 'Go to Login',
                    onAction: () => { stopCamera(); navigateTo('/login'); }
                });
            }
        } finally {
            setLoadingPreview(false);
        }
    };

    useEffect(() => {
        if (showQuestionPreview && interviewStarted && currentQuestionNumber > 0 && !currentQuestion) {
            fetchQuestionPreview(currentQuestionNumber);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestionNumber, interviewStarted, showQuestionPreview]);

    const handleStartQuestion = async (questionNumber) => {
        setStartingQuestion(true);
        setSessionAlert(null);
        setPreviewQuestionData(null);

        try {
            // Ensure media permissions are granted before starting question
            if (!mediaPermissions.granted) {
                const stream = await requestMediaPermissions();
                if (!stream) {
                    throw new Error('Media permissions are required to start the question');
                }
            }

            const response = await API.post(
                apiEndpoints.startQuestion.replace(':questionNumber', questionNumber),
                {},
                { skipAuthRedirect: true }
            );

            if (response.data && response.data.question) {
                const questionData = response.data.question;
                console.log('✅ Question data received:', questionData);
                console.log('🔑 AssemblyAI key:', questionData.assemblyai_key ? 'Present' : 'Missing');

                // Reset per-question recording/transcription state.
                // Chunks must be cleared here too: if startRecording fails before
                // its own clear, the fallback in stopRecording must never see a
                // previous question's (or practice) chunks.
                reconnectAttemptsRef.current = 0;
                mediaFailureRef.current = false;
                finalRecordingRef.current = null;
                recordedChunksRef.current = [];
                turnsMapRef.current = new Map();
                partialTurnRef.current = '';
                transcriptAccumulatorRef.current = '';
                setTranscript('');

                if (!questionData.assemblyai_key) {
                    console.error('❌ No AssemblyAI key provided in question response');
                    setTranscriptionStatus('unavailable');
                    setSessionAlert({
                        severity: 'warning',
                        message: 'Transcription service unavailable. Your answer is still being recorded.'
                    });
                }

                // On recovery (already_started) the server reports time since the
                // original start; drive the wall-clock countdown from that
                const totalSeconds = Math.round(questionData.time_limit * 60);
                const secondsElapsed = response.data.already_started
                    ? Math.floor(response.data.seconds_elapsed || 0)
                    : 0;

                setCurrentQuestion(questionData);
                setQuestionStartTime(new Date(Date.now() - secondsElapsed * 1000));
                startQuestionTimer(Math.max(0, totalSeconds - secondsElapsed));

                // Start recording with AssemblyAI transcription
                console.log('🎬 Starting recording with AssemblyAI key:', questionData.assemblyai_key ? 'Provided' : 'Not provided');
                proctorRef.current.startQuestion(questionNumber, !!response.data.already_started);
                await startRecording(questionData.assemblyai_key);
            } else {
                console.error('❌ No question data in response:', response.data);
                setSessionAlert({
                    severity: 'blocking',
                    message: 'Failed to start question. Please try again.',
                    actionLabel: 'Retry',
                    onAction: () => handleStartQuestion(questionNumber)
                });
            }

        } catch (error) {
            console.error("Error starting question:", error);
            const status = error.response?.status;
            const errorCode = error.response?.data?.error_code;
            if (status === 401) {
                setSessionAlert({
                    severity: 'blocking',
                    message: 'Your session has expired. Your submitted answers are saved — log in again and you will continue from your next unanswered question.',
                    actionLabel: 'Go to Login',
                    onAction: () => { stopCamera(); navigateTo('/login'); }
                });
            } else if (status === 410 && errorCode === 'time_expired') {
                setSessionAlert({
                    severity: 'blocking',
                    message: `Question ${questionPositionOf(questionNumber)}'s time ran out before it could be resumed. It has been forfeited — your other answers are not affected.`,
                    actionLabel: 'Continue',
                    onAction: skipCurrentQuestion
                });
            } else if (status === 403 && errorCode === 'video_window_closed') {
                /* The window is shut for good: no later attempt can open it, so the
                   alert carries no action. A Retry here would be a button that
                   cannot work. */
                setSessionAlert({
                    severity: 'blocking',
                    message: 'The video submission window for this work trial has closed, so no further recording can be made. Any answers you already submitted are saved.'
                });
            } else {
                setSessionAlert({
                    severity: 'blocking',
                    message: error.response?.data?.error || error.message || 'Failed to start question. Please try again.',
                    actionLabel: 'Retry',
                    onAction: () => handleStartQuestion(questionNumber)
                });
            }
        } finally {
            setStartingQuestion(false);
        }
    };

    // Wall-clock countdown: background-tab throttling stretches interval ticks,
    // so remaining time is always recomputed from the stored deadline
    const updateTimeRemaining = () => {
        if (questionDeadlineRef.current === null) return;
        const remaining = Math.max(0, Math.ceil((questionDeadlineRef.current - Date.now()) / 1000));
        setTimeRemaining(remaining);
        if (remaining <= 0 && !timerExpiredRef.current) {
            timerExpiredRef.current = true;
            questionDeadlineRef.current = null;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            // Short grace for in-flight transcription; the submit flow also
            // waits for the socket to finalize before reading the transcript
            setTimeout(() => {
                if (isPracticeModeRef.current) {
                    handleCompletePractice();
                } else if (!submitInFlightRef.current) {
                    handleAutoSubmitWithFinalTranscript();
                }
            }, 500);
        }
    };

    const startQuestionTimer = (seconds) => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        timerExpiredRef.current = false;
        questionDeadlineRef.current = Date.now() + Math.round(seconds) * 1000;
        setTimeRemaining(Math.max(0, Math.round(seconds)));
        intervalRef.current = setInterval(updateTimeRemaining, 1000);
    };

    // Recompute remaining time immediately when the tab becomes visible again
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                updateTimeRemaining();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Helper function to get supported MIME type
    // MP4 is preferred because browsers can determine video duration instantly
    // from the MP4 container (moov atom). WebM recorded by MediaRecorder often
    // has unknown duration metadata which causes playback issues.
    // Chrome 122+, Edge, and Safari all support MP4 recording.
    // Firefox falls back to WebM (the backend converts it to MP4 automatically).
    const getSupportedMimeType = () => {
        const types = [
            'video/mp4;codecs=avc1,mp4a',
            'video/mp4;codecs=h264,aac',
            'video/mp4',
            'video/webm;codecs=vp9,opus',
            'video/webm;codecs=vp8,opus',
            'video/webm',
        ];
        
        for (const type of types) {
            if (MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }
        
        // Fallback - this should work in most browsers
        return '';
    };

    const startRecording = async (assemblyaiKey) => {
        try {
            console.log('🎬 Starting recording process...');
            console.log('🔑 AssemblyAI key available:', !!assemblyaiKey);
            
            // Get the current stream or request a new one if not available
            let stream = videoRef.current?.srcObject;
            
            if (!stream) {
                console.log('📹 Requesting new media stream with selected devices...');
                // Request new media stream using selected devices
                stream = await navigator.mediaDevices.getUserMedia(getMediaConstraints());
                
                // Set the stream to video element
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    console.log('✅ Video stream set to video element');
                }
            }
            
            if (!stream) {
                throw new Error('Failed to get media stream');
            }
            
            // Ensure audio level monitoring is active for the current stream
            if (!audioLevelRef.current) {
                startAudioLevelMonitoring(stream);
            }
            
            // Log stream info
            const audioTracks = stream.getAudioTracks();
            const videoTracks = stream.getVideoTracks();
            console.log(`📊 Stream info: ${videoTracks.length} video tracks, ${audioTracks.length} audio tracks`);
            if (audioTracks.length > 0) {
                console.log('🎤 Audio track settings:', audioTracks[0].getSettings());
            }
            
            recordedChunksRef.current = [];
            mediaFailureRef.current = false;

            // Get supported MIME type for this browser
            const mimeType = getSupportedMimeType();
            // Capped bitrates: browser defaults (~2.5 Mbps video) produce ~19MB/min,
            // which slows both candidate uploads and recruiter playback.
            const options = {
                videoBitsPerSecond: 1_200_000,
                audioBitsPerSecond: 96_000,
                ...(mimeType ? { mimeType } : {}),
            };

            console.log('📱 Using MIME type:', mimeType || 'default');

            const mediaRecorder = new MediaRecorder(stream, options);

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    recordedChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onerror = (event) => {
                const errorName = (event.error && event.error.name) || 'unknown error';
                handleMediaFailure(`Recording failed (${errorName})`);
            };

            mediaRecorderRef.current = mediaRecorder;
            mediaRecorder.start(1000); // Collect data every second
            console.log('🔴 Media recorder started');
            
            // Set up AssemblyAI transcription FIRST
            if (assemblyaiKey && assemblyaiKey !== 'practice') {
                console.log('🔗 Setting up AssemblyAI transcription...');
                
                // Guard against both the callback and fallback starting audio simultaneously
                let audioStreamStarted = false;
                
                const startAudioOnce = async () => {
                    if (audioStreamStarted || audioRecorderRef.current) return;
                    audioStreamStarted = true;
                    await startAudioRecording(stream);
                };
                
                const onWebSocketConnected = async () => {
                    console.log('🎙️ WebSocket connected, starting audio recording...');
                    await startAudioOnce();
                };
                
                const socket = setupAssemblyAITranscription(assemblyaiKey, onWebSocketConnected);
                
                if (socket) {
                    // Fallback polling in case the onopen callback doesn't fire
                    const waitForConnection = async (maxRetries = 10, retryDelay = 500) => {
                        for (let i = 0; i < maxRetries; i++) {
                            await new Promise(resolve => setTimeout(resolve, retryDelay));
                            
                            if (assemblyAiSocketRef.current && assemblyAiSocketRef.current.readyState === WebSocket.OPEN) {
                                if (audioRecorderRef.current || audioStreamStarted) {
                                    console.log(`✅ WebSocket connected and audio recording already started`);
                                    return true;
                                }
                                
                                console.log(`🎙️ WebSocket connected after ${(i + 1) * retryDelay}ms (fallback), starting audio recording...`);
                                await startAudioOnce();
                                return true;
                            } else if (assemblyAiSocketRef.current) {
                                const state = assemblyAiSocketRef.current.readyState;
                                const stateNames = {
                                    [WebSocket.CONNECTING]: 'CONNECTING',
                                    [WebSocket.OPEN]: 'OPEN',
                                    [WebSocket.CLOSING]: 'CLOSING',
                                    [WebSocket.CLOSED]: 'CLOSED'
                                };
                                console.log(`⏳ WebSocket state: ${stateNames[state] || state}, retry ${i + 1}/${maxRetries}...`);
                            } else {
                                console.log(`⏳ WebSocket not initialized, retry ${i + 1}/${maxRetries}...`);
                            }
                        }
                        
                        console.error('❌ WebSocket failed to connect after all retries, transcription unavailable');
                        return false;
                    };
                    
                    waitForConnection().catch(error => {
                        console.error('❌ Error waiting for WebSocket connection:', error);
                    });
                } else {
                    console.error('❌ Failed to set up AssemblyAI WebSocket');
                }
            } else if (assemblyaiKey === 'practice') {
                console.log('🎯 Practice mode - mock transcription disabled (no transcription during practice)');
                // Don't start any transcription during practice
            } else if (!assemblyaiKey) {
                console.warn('⚠️ No AssemblyAI key provided, transcription will not be available');
            }

            isRecordingRef.current = true;
            setIsRecording(true);
            attachMediaFailureHandlers(stream);
            console.log('✅ Recording started successfully');

        } catch (error) {
            console.error('❌ Error starting recording:', error);
            let errorMessage = 'Failed to start recording. ';

            if (error.name === 'NotAllowedError') {
                errorMessage += 'Please allow camera and microphone access in your browser settings.';
            } else if (error.name === 'NotFoundError') {
                errorMessage += 'No camera or microphone found. Please connect the required devices.';
            } else if (error.name === 'NotReadableError') {
                errorMessage += 'Your camera or microphone is already in use by another application.';
            } else if (error.name === 'NotSupportedError') {
                errorMessage += 'Your browser does not support video recording. Please try using a different browser.';
            } else {
                errorMessage += 'Please check your devices and try again.';
            }

            // Re-starting the question is safe: recovery start_question does not reset state
            setSessionAlert({
                severity: 'blocking',
                message: errorMessage,
                actionLabel: 'Retry',
                onAction: isPracticeModeRef.current
                    ? handleStartPractice
                    : () => handleStartQuestion(currentQuestionNumberRef.current)
            });
            isRecordingRef.current = false;
            setIsRecording(false);
        }
    };

    // Camera unplug / permission revocation / recorder death is otherwise silent:
    // surface it, stop claiming the recording is live, and freeze the countdown
    // so the candidate can still submit what was captured
    const handleMediaFailure = (message) => {
        if (mediaFailureRef.current) return;
        mediaFailureRef.current = true;
        isRecordingRef.current = false;
        setIsRecording(false);
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        questionDeadlineRef.current = null;
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            try { mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }
        }
        // Never replace a blocking alert (e.g. a failed submit's Retry) with a
        // dismissible warning
        setSessionAlert(prev => (prev && prev.severity === 'blocking') ? prev : ({
            severity: 'warning',
            message: `${message}. Recording has stopped — you can still submit what was captured so far.`
        }));
    };

    const attachMediaFailureHandlers = (stream) => {
        stream.getTracks().forEach((track) => {
            const deviceLabel = track.kind === 'video' ? 'camera' : 'microphone';
            // track.stop() (our own cleanup) does not fire onended
            track.onended = () => {
                proctorRef.current.mediaTrackEvent('stream-track-ended', track.kind);
                handleMediaFailure(`Your ${deviceLabel} stopped or was disconnected`);
            };
            track.onmute = () => {
                proctorRef.current.mediaTrackEvent('camera-muted', track.kind);
                if (!isRecordingRef.current) return;
                setSessionAlert(prev => (prev && prev.severity === 'blocking') ? prev : ({
                    severity: 'warning',
                    id: 'device-muted',
                    message: `Your ${deviceLabel} signal was interrupted. Check that the device is connected and not in use by another application.`
                }));
            };
            track.onunmute = () => {
                proctorRef.current.mediaTrackEvent('camera-unmuted', track.kind);
                setSessionAlert(prev => (prev && prev.id === 'device-muted' ? null : prev));
            };
        });
    };

    // Create a separate audio recording for AssemblyAI
    const startAudioRecording = async (stream) => {
        try {
            const audioTracks = stream.getAudioTracks();
            if (audioTracks.length === 0) {
                console.error('[AssemblyAI] No audio tracks in stream');
                return;
            }
            
            // Create audio-only stream for AssemblyAI
            const audioStream = new MediaStream();
            stream.getAudioTracks().forEach(track => {
                audioStream.addTrack(track);
            });

            // Create audio context with 16kHz sample rate to match AssemblyAI
            const audioContext = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: 16000
            });
            
            // Resume audio context if suspended (required for autoplay policies)
            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }
            
            const source = audioContext.createMediaStreamSource(audioStream);
            
            // Create a script processor for processing audio data (4096 buffer size for smooth processing)
            // Note: ScriptProcessorNode is deprecated but still widely supported
            const processor = audioContext.createScriptProcessor(4096, 1, 1);
            
            processor.onaudioprocess = (event) => {
                try {
                    // Check if WebSocket is available and open
                    if (!assemblyAiSocketRef.current || assemblyAiSocketRef.current.readyState !== WebSocket.OPEN) {
                        return;
                    }
                    
                    const inputData = event.inputBuffer.getChannelData(0);
                    
                    // Convert Float32Array to Int16Array for AssemblyAI (PCM16 format)
                    const pcmData = new Int16Array(inputData.length);
                    for (let i = 0; i < inputData.length; i++) {
                        const sample = Math.max(-1, Math.min(1, inputData[i]));
                        pcmData[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
                    }
                    
                    // Send raw binary PCM16 audio data to AssemblyAI v3
                    // v3 Universal Streaming expects binary WebSocket frames, not base64 JSON
                    assemblyAiSocketRef.current.send(pcmData.buffer);
                } catch (error) {
                    // Silently ignore send errors during recording
                }
            };
            
            source.connect(processor);
            processor.connect(audioContext.destination);
            
            // Store audio context info for cleanup
            audioRecorderRef.current = {
                audioContext,
                processor,
                source,
                stopRecording: () => {
                    try {
                        if (processor) processor.disconnect();
                        if (source) source.disconnect();
                        if (audioContext && audioContext.state !== 'closed') audioContext.close();
                    } catch (error) {
                        // Ignore cleanup errors
                    }
                }
            };
            
        } catch (error) {
            console.error('[AssemblyAI] Error starting audio recording:', error);
        }
    };

    const stopRecording = async () => {
        isRecordingRef.current = false;
        setIsRecording(false);

        // Stop streaming audio to AssemblyAI before terminating the session
        if (audioRecorderRef.current) {
            audioRecorderRef.current.stopRecording();
            audioRecorderRef.current = null;
        }

        // v3: send Terminate, then wait (bounded) for the final turn/termination
        // messages so in-flight transcript tails are not dropped
        if (assemblyAiSocketRef.current) {
            try {
                if (assemblyAiSocketRef.current.readyState === WebSocket.OPEN) {
                    assemblyAiSocketRef.current.send(JSON.stringify({ type: 'Terminate' }));
                    await waitForTranscriptFinal(2000);
                }
            } catch (e) { /* ignore */ }
            // onclose may have already cleared the ref during the wait
            if (assemblyAiSocketRef.current) {
                try { assemblyAiSocketRef.current.close(1000); } catch (e) { /* ignore */ }
                assemblyAiSocketRef.current = null;
            }
        }
        setTranscriptionStatus('idle');

        return new Promise((resolve) => {
            const finalize = () => {
                // Use the same MIME type that was used for recording
                const mimeType = (mediaRecorderRef.current && mediaRecorderRef.current.mimeType) ||
                    getSupportedMimeType() || 'video/mp4';
                const blob = new Blob(recordedChunksRef.current, { type: mimeType });
                // Cache blob + transcript snapshot so a failed submit is retryable
                // after the recorder has gone inactive
                finalRecordingRef.current = {
                    blob,
                    transcript: transcriptAccumulatorRef.current || ''
                };
                resolve(blob);
            };

            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.onstop = finalize;
                mediaRecorderRef.current.stop();
            } else if (finalRecordingRef.current && finalRecordingRef.current.blob) {
                resolve(finalRecordingRef.current.blob);
            } else if (recordedChunksRef.current.length > 0) {
                // Recorder already died (e.g. device failure) but chunks were captured
                finalize();
            } else {
                resolve(null);
            }
        });
    };

    const uploadVideoFile = async (uploadUrl, videoBlob) => {
        return new Promise((resolve, reject) => {
            try {
                const xhr = new XMLHttpRequest();
                
                xhr.open('PUT', uploadUrl, true);
                
                // Set the content type header based on the actual blob type
                // Normalize the content type to match what the backend expects
                let contentType = videoBlob.type || 'video/mp4';
                if (contentType.startsWith('video/mp4')) {
                    contentType = 'video/mp4';
                } else if (contentType.startsWith('video/webm')) {
                    contentType = 'video/webm';
                }
                xhr.setRequestHeader('Content-Type', contentType);
                
                xhr.onload = () => {
                    if (xhr.status === 200 || xhr.status === 201) {
                        console.log('Video uploaded successfully');
                        resolve();
                    } else {
                        const errorDetails = {
                            status: xhr.status,
                            statusText: xhr.statusText,
                            response: xhr.responseText,
                            url: uploadUrl,
                            blobSize: videoBlob.size,
                            blobType: videoBlob.type
                        };
                        console.error('Upload failed:', errorDetails);
                        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
                    }
                };
                
                xhr.onerror = (e) => {
                    const errorDetails = {
                        error: e,
                        url: uploadUrl,
                        blobSize: videoBlob.size,
                        blobType: videoBlob.type
                    };
                    console.error('Network error during upload:', errorDetails);
                    reject(new Error('Network error during upload'));
                };
                
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        const percentComplete = (event.loaded / event.total) * 100;
                        console.log(`Upload progress: ${percentComplete.toFixed(2)}%`);
                    }
                };
                
                // Log the upload attempt
                console.log('Attempting to upload to:', uploadUrl);
                console.log('Video blob size:', videoBlob.size, 'bytes');
                console.log('Video blob type:', videoBlob.type);
                console.log('Content-Type header:', contentType);
                
                // Send the blob directly
                xhr.send(videoBlob);
            } catch (error) {
                console.error('Error in uploadVideoFile:', error);
                reject(new Error(`Video upload failed: ${error.message}`));
            }
        });
    };

    const handleAutoSubmitWithFinalTranscript = async () => {
        // The submit flow waits for the socket to finalize, then reads the
        // accumulator — no sentinel fallbacks, empty transcripts are valid
        await handleSubmitQuestion(true);
    };

    const handleSubmitQuestion = async (isAutoSubmit = false) => {
        // Ref-based single-flight guard: state updates are async, so a manual
        // click racing the auto-submit grace period must be blocked synchronously
        if (submitInFlightRef.current) return;
        submitInFlightRef.current = true;
        setSubmittingQuestion(true);
        setSessionAlert(null);

        try {
            // Stop timer
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            questionDeadlineRef.current = null;

            // Idempotent: returns the cached final blob on retries
            const videoBlob = await stopRecording();

            const finalTranscript = finalRecordingRef.current
                ? (finalRecordingRef.current.transcript || '')
                : (transcriptAccumulatorRef.current || '');

            console.log('Submitting transcript:', finalTranscript);

            const videoContentType = (videoBlob && videoBlob.type) || 'video/mp4';

            // Idempotent on the server: a retry gets already_submitted:true plus
            // a fresh upload_url for the same blob path
            const submitResponse = await API.post(
                apiEndpoints.submitQuestion.replace(':questionNumber', currentQuestionNumberRef.current),
                {
                    transcript: finalTranscript,
                    content_type: videoContentType
                },
                {
                    headers: {
                        "Content-Type": "application/json"
                    },
                    skipAuthRedirect: true
                }
            );

            if (submitResponse.data && submitResponse.data.upload_url && videoBlob && videoBlob.size > 0) {
                await uploadVideoFile(submitResponse.data.upload_url, videoBlob);
            }

            // Reload only after BOTH the submit POST and the video upload succeeded
            console.log('Question submitted successfully');
            // Beacon-flush telemetry now — the reload below aborts in-flight XHRs
            proctorRef.current.questionSubmitted(!!isAutoSubmit);
            console.log('🛑 Stopping camera after submission...');
            stopCamera();

            // Small delay to ensure cleanup completes
            await new Promise(resolve => setTimeout(resolve, 100));

            console.log('🔄 Reloading page after successful submission...');
            // Mark this reload as intentional and drop the beforeunload guard
            // so the transition is silent and not treated as an interruption
            sessionStorage.setItem(transitionFlagKey, '1');
            proctorRef.current.markExpectedUnload();
            releaseUnloadGuard();
            window.location.reload();

        } catch (error) {
            console.error("Error submitting question:", error);
            setSubmittingQuestion(false);
            const status = error.response?.status;
            const errorCode = error.response?.data?.error_code;
            if (status === 401) {
                setSessionAlert({
                    severity: 'blocking',
                    message: 'Your session expired, so this answer could not be submitted. Your earlier answers are saved — log in again to continue from your next unanswered question.',
                    actionLabel: 'Go to Login',
                    onAction: () => { stopCamera(); navigateTo('/login'); }
                });
            } else if (status === 403 && errorCode === 'time_limit_exceeded') {
                setSessionAlert({
                    severity: 'blocking',
                    message: `Your answer reached the server after question ${questionPositionOf(currentQuestionNumberRef.current)}'s time limit and could not be accepted. The question is forfeited — your other answers are not affected.`,
                    actionLabel: 'Continue',
                    onAction: skipCurrentQuestion
                });
            } else if (status === 403 && errorCode === 'video_window_closed') {
                /* The window is shut for good: no later attempt can open it, so the
                   alert carries no action. A Retry here would be a button that
                   cannot work. */
                setSessionAlert({
                    severity: 'blocking',
                    message: 'The video submission window for this work trial closed before this recording began, so this answer could not be accepted. The window cannot reopen — your earlier answers are saved.'
                });
            } else {
                setSessionAlert({
                    severity: 'blocking',
                    message: error.response?.data?.error ||
                        'Your answer could not reach the server — this is usually a connection problem. Your recording and transcript are still held in this tab; do not close or reload this page. Check your connection and retry.',
                    actionLabel: 'Retry',
                    onAction: () => handleSubmitQuestion(isAutoSubmit)
                });
            }
        } finally {
            submitInFlightRef.current = false;
        }
    };

    const stopCamera = () => {
        console.log('🛑 Stopping camera and all media streams...');
        
        // Stop audio level monitoring
        stopAudioLevelMonitoring();
        
        // Stop recording if active (check both state and recorder state)
        const isCurrentlyRecording = isRecording || 
            (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive');
        
        if (isCurrentlyRecording) {
            console.log('🛑 Stopping active recording...');
            stopRecording().catch(error => {
                console.error('Error stopping recording:', error);
            });
        }
        
        // Stop all tracks from video element
        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject;
            const tracks = stream.getTracks();
            console.log(`🛑 Stopping ${tracks.length} tracks from video element`);
            tracks.forEach(track => {
                track.stop();
                console.log(`✅ Stopped track: ${track.kind} (${track.label || 'unnamed'})`);
            });
            videoRef.current.srcObject = null;
        }
        
        // Stop any pending stream
        if (pendingStreamRef.current) {
            const stream = pendingStreamRef.current;
            const tracks = stream.getTracks();
            console.log(`🛑 Stopping ${tracks.length} tracks from pending stream`);
            tracks.forEach(track => {
                track.stop();
                console.log(`✅ Stopped pending track: ${track.kind} (${track.label || 'unnamed'})`);
            });
            pendingStreamRef.current = null;
        }
        
        // Stop audio recording for AssemblyAI
        if (audioRecorderRef.current) {
            console.log('🛑 Stopping audio recording for AssemblyAI...');
            try {
                audioRecorderRef.current.stopRecording();
                audioRecorderRef.current = null;
            } catch (error) {
                console.error('Error stopping audio recording:', error);
            }
        }
        
        // Close AssemblyAI WebSocket
        if (assemblyAiSocketRef.current) {
            console.log('🛑 Closing AssemblyAI WebSocket...');
            try {
                assemblyAiSocketRef.current.close();
                assemblyAiSocketRef.current = null;
            } catch (error) {
                console.error('Error closing WebSocket:', error);
            }
        }
        
        // Stop media recorder
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            console.log('🛑 Stopping media recorder...');
            try {
                mediaRecorderRef.current.stop();
            } catch (error) {
                console.error('Error stopping media recorder:', error);
            }
        }
        
        // Clear video element
        if (videoRef.current) {
            videoRef.current.srcObject = null;
            videoRef.current.load(); // Reset video element
        }
        
        console.log('✅ Camera and all media streams stopped');
    };

    const handleEndInterview = async () => {
        if (endInFlightRef.current) return;
        endInFlightRef.current = true;
        setEndingInterview(true);
        setEndFailed(false);
        setSessionAlert(null);

        try {
            // Idempotent on the server (already_submitted flag on retries)
            const response = await API.post(apiEndpoints.end, {}, { skipAuthRedirect: true });

            // Any resolved 2xx is success — a falsy body must not strand the
            // finalize stage on its no-button spinner state.
            const data = response.data || {};
            // Server-side truth about questions forfeited at finalization
            const forfeited = Array.isArray(data.forfeited_questions)
                ? data.forfeited_questions.map(Number).filter((n) => !Number.isNaN(n))
                : [];
            setForfeitedQuestions(forfeited);

            proctorRef.current.interviewEnded();
            stopCamera();
            setInterviewStarted(false);
            setCurrentQuestion(null);
            setCurrentQuestionNumber(null);
            setSessionAlert(null);
            setSuccessMessage(navigationConfig.successMessage);

        } catch (error) {
            console.error("Error ending interview:", error);
            setEndFailed(true);
            if (error.response?.status === 401) {
                setSessionAlert({
                    severity: 'blocking',
                    message: L.endExpired,
                    actionLabel: 'Go to Login',
                    onAction: () => { stopCamera(); navigateTo('/login'); }
                });
            } else {
                // The finalize button below doubles as the retry action
                setSessionAlert({
                    severity: 'blocking',
                    message: error.response?.data?.error || L.endFailed
                });
            }
        } finally {
            endInFlightRef.current = false;
            setEndingInterview(false);
        }
    };

    const formatTime = (seconds) => {
        const roundedSeconds = Math.round(seconds);
        const minutes = Math.floor(roundedSeconds / 60);
        const remainingSeconds = roundedSeconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const getTimerClassName = () => {
        if (timeRemaining <= 30) {
            return 'timer danger';
        } else if (timeRemaining <= 60) {
            return 'timer warning';
        }
        return 'timer';
    };

    // Subtle live transcription status chip for the transcript panel
    const renderTranscriptionStatus = () => {
        const statusStyles = {
            active: { label: 'Live', color: 'var(--ok)', background: 'var(--ok-tint)' },
            reconnecting: { label: 'Reconnecting…', color: 'var(--warn)', background: 'var(--warn-tint)' },
            unavailable: { label: 'Unavailable — recording continues', color: 'var(--muted)', background: 'var(--surface-sunken)' }
        };
        const status = statusStyles[transcriptionStatus];
        if (!status) return null;
        return (
            <span style={{
                display: 'inline-block',
                marginLeft: 'var(--space-2)',
                padding: '2px 8px',
                borderRadius: 'var(--radius-pill)',
                fontSize: 'var(--text-xs)',
                fontWeight: 500,
                verticalAlign: 'middle',
                textTransform: 'none',
                letterSpacing: 'normal',
                color: status.color,
                background: status.background
            }}>
                {status.label}
            </span>
        );
    };

    const answeredCount = Object.keys(interview?.question_submission_dates || {}).length;
    const totalQuestions = interview?.question_count || interview?.question_numbers?.length || 0;
    // Client-side skips and server-reported forfeits, as display positions
    const forfeitedPositions = [...new Set([...skippedQuestions, ...forfeitedQuestions])]
        .map((n) => questionPositionOf(n))
        .sort((a, b) => a - b);
    const resumeElapsedSeconds = resumeContext?.startedAt
        ? Math.max(0, Math.floor((resumeNow - resumeContext.startedAt.getTime()) / 1000))
        : 0;
    // Completion stage: everything answerable is answered but /end not yet done
    const atCompletionStage = interviewStarted && !currentQuestion && !currentQuestionNumber;
    // Auto-end only when the interview data proves every known question is
    // submitted or client-skipped — a malformed/empty question list must not
    // trigger an /end that could forfeit outstanding questions server-side.
    const allQuestionsAccounted = Boolean(
        interview?.question_numbers?.length &&
        interview.question_numbers.every((q) =>
            (interview.question_submission_dates && interview.question_submission_dates[String(q)]) ||
            skippedQuestions.includes(q)
        )
    );

    // Auto-finalize: reaching the finalize stage (every question submitted or
    // skipped — including a resume where all questions were already submitted)
    // calls the idempotent /end endpoint automatically. endInFlightRef guards
    // double-fire; on failure the stage's button remains as a manual retry.
    useEffect(() => {
        if (atCompletionStage && allQuestionsAccounted && !successMessage) {
            handleEndInterview();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [atCompletionStage, allQuestionsAccounted]);

    if (loading) {
        return (
            <div className="interview-page">
                <div className="loading-container">
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                        <p>{L.loading}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Full-page error — initial load failures only (not found / access denied /
    // fetch failure). In-session errors render inline via InterviewAlert.
    if (error) {
        return (
            <div className="interview-page">
                <div className="error-container">
                    <h2>Error</h2>
                    <p>{error}</p>
                    <button 
                        className="btn btn-primary"
                        onClick={() => {
                            console.log('🔙 Back button clicked (error page), stopping camera...');
                            stopCamera();
                            navigateTo(navigationConfig.backPath);
                        }}
                    >
                        {navigationConfig.backText}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="interview-page">
            <div className="interview-container">
                
                {/* Interview Header */}
                <div className="interview-header">
                    <div className="header-top">
                        <button
                            className="back-button"
                            onClick={() => {
                                // Same stakes as reload/close (which the beforeunload
                                // guard covers): an engaged question's answer is lost
                                // and its timer keeps running
                                if (questionEngaged && !window.confirm(
                                    L.leaveConfirm
                                )) {
                                    return;
                                }
                                console.log('🔙 Back button clicked, stopping camera and cleaning up...');

                                // Stop practice mode if active
                                if (isPracticeMode || showPracticeStep) {
                                    console.log('🛑 Stopping practice mode...');
                                    setIsPracticeMode(false);
                                    setShowPracticeStep(false);
                                    
                                    // Stop timer if running
                                    if (intervalRef.current) {
                                        clearInterval(intervalRef.current);
                                        intervalRef.current = null;
                                    }
                                }
                                
                                // Stop camera and all streams
                                stopCamera();
                                
                                // Navigate back
                                navigateTo(navigationConfig.backPath);
                            }}
                        >
                            <ArrowLeft size={14} /> {navigationConfig.backText}
                        </button>
                    </div>
                    
                    <div className="interview-meta">
                        <h1>{interviewType}</h1>
                        <h2>{L.tagline}</h2>
                        
                        <div className="meta-info">
                            {interview?.deadline && (
                                <span className="meta-item">
                                    Deadline: {formatToLocalDateTime(interview.deadline)}
                                </span>
                            )}
                            {interview?.question_count > 0 && (
                                <span className="meta-item">
                                    Questions: {interview.question_count}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Interview Content */}
                <div className="interview-content">

                    {/* Inline alert for pre-interview phases (setup/confirmation/practice) */}
                    {!interviewStarted && !successMessage && (
                        <InterviewAlert alert={sessionAlert} onDismiss={() => setSessionAlert(null)} labels={L.alert} />
                    )}

                    {/* Post-submission success screen */}
                    {successMessage && (
                        <div className="interview-complete">
                            <div className="complete-seal"><Check size={26} /></div>
                            <p className="complete-eyebrow">{interviewType}</p>
                            <h2>{L.submittedHeading}</h2>
                            <p className="complete-summary">{answeredCount} of {totalQuestions} questions answered.</p>
                            <p className="complete-body">{successMessage}</p>
                            {forfeitedPositions.length > 0 && (
                                <div className="complete-forfeits">
                                    <p>
                                        {forfeitedPositions.length === 1
                                            ? `Question ${forfeitedPositions[0]} ran out of time before it was submitted and was forfeited.`
                                            : `Questions ${forfeitedPositions.join(', ')} ran out of time before they were submitted and were forfeited.`}
                                        {' '}Forfeited questions are recorded as unanswered.
                                    </p>
                                </div>
                            )}
                            <div className="interview-actions">
                                <button
                                    className="btn btn-large"
                                    onClick={() => navigateTo(navigationConfig.successPath)}
                                >
                                    {navigationConfig.successButtonText}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ═══ PAGE 1: Device Setup ═══ */}
                    {!interviewStarted && !successMessage && !showPracticeStep && !showConfirmationStep && (
                        <div className="interview-setup">
                            <InterviewStepper activeIndex={0} labels={L.stepper} />
                            <div className="setup-card">
                                <h2>{L.welcome(interviewType)}</h2>
                                <div className="interview-info">
                                    <div className="info-item">
                                        <span className="info-icon"><ClipboardList size={18} /></span>
                                        <div>
                                            <strong>{interview?.question_count || 0} Questions</strong>
                                            <p>You'll answer each question in sequence, with time to prepare between each one.</p>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-icon"><Video size={18} /></span>
                                        <div>
                                            <strong>Video Recording</strong>
                                            <p>{L.videoRuleBody}</p>
                                        </div>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-icon"><Clock size={18} /></span>
                                        <div>
                                            <strong>Timed Responses</strong>
                                            <p>Each question has a specific time limit to ensure fair and consistent responses.</p>
                                        </div>
                                    </div>
                                    {interview?.deadline && (
                                        <div className="info-item">
                                            <span className="info-icon"><Calendar size={18} /></span>
                                            <div>
                                                <strong>Submission Deadline</strong>
                                                <p>{formatToLocalDateTime(interview.deadline)}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="permissions-info">
                                    <h3>Required Permissions</h3>
                                    <p>{L.permissionsBody}</p>

                                    <DeviceCheckPanel
                                        videoRef={videoRef}
                                        mediaTestComplete={mediaTestComplete}
                                        mediaTestError={mediaTestError}
                                        onTest={testMediaDevices}
                                        availableCameras={availableCameras}
                                        availableMicrophones={availableMicrophones}
                                        selectedCameraId={selectedCameraId}
                                        selectedMicrophoneId={selectedMicrophoneId}
                                        onCameraChange={handleCameraChange}
                                        onMicrophoneChange={handleMicrophoneChange}
                                        audioLevel={audioLevel}
                                        labels={L.deviceCheck}
                                    />
                                </div>

                                <div className="wizard-footer">
                                    <div className="wizard-footer-left">
                                        {!mediaTestComplete && (
                                            <p className="wizard-hint">Test your camera and microphone to continue.</p>
                                        )}
                                    </div>
                                    <button
                                        className="btn btn-large"
                                        onClick={handleProceedToConfirmation}
                                        disabled={!mediaTestComplete}
                                    >
                                        Continue <ArrowRight size={14} style={{ marginLeft: 4 }} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══ PAGE 2: Confirmation & Disclaimers ═══ */}
                    {!interviewStarted && !successMessage && !showPracticeStep && showConfirmationStep && (
                        <div className="interview-setup">
                            <InterviewStepper activeIndex={1} labels={L.stepper} />
                            <div className="setup-card confirmation-card">
                                <h2>Before You Begin</h2>
                                <p className="confirmation-subtitle">Please read each item carefully. You must acknowledge all conditions before starting.</p>

                                <div className="disclaimers-section">
                                    <div className="disclaimer-box disclaimer-critical">
                                        <h3><AlertTriangle size={16} style={{ marginRight: 6 }} /> ONE TAKE PER QUESTION</h3>
                                        <p>Each answer is recorded in a single take. Once a question starts, its timer is running and that recording is your answer — there are no retakes and no re-recordings.</p>
                                    </div>

                                    <div className="disclaimer-box disclaimer-danger">
                                        <h3><Clock size={16} style={{ marginRight: 6 }} /> TIMERS DO NOT PAUSE</h3>
                                        <p>{L.timersBody}</p>
                                    </div>

                                    <div className="disclaimer-box disclaimer-warning">
                                        <h3><MessageSquare size={16} style={{ marginRight: 6 }} /> SPEAK CLEARLY</h3>
                                        <p>Your responses are transcribed in real time. Speak clearly, at a moderate pace, and directly into your microphone — poor audio lowers your transcript quality and can affect your evaluation.</p>
                                    </div>

                                    <div className="disclaimer-box disclaimer-info">
                                        <h3><Monitor size={16} style={{ marginRight: 6 }} /> YOUR DEVICE, YOUR RESPONSIBILITY</h3>
                                        <p>Make sure your internet connection, camera, microphone, and device are working before you begin. Time lost to disconnections, device failures, or crashes on your end is not restored, and technical issues are not grounds for a retake.</p>
                                    </div>

                                    <div className="disclaimer-box disclaimer-info">
                                        <h3><Eye size={16} style={{ marginRight: 6 }} /> ACTIVITY IS LOGGED WHILE RECORDING</h3>
                                        <p>{L.loggingBody}</p>
                                    </div>
                                </div>

                                <div className="disclaimer-acknowledge">
                                    <label className="disclaimer-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={disclaimerAccepted}
                                            onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                                        />
                                        <span>I understand that each answer is one take, that timers do not pause if I leave, that a question not submitted in its time window is forfeited, and that activity signals are logged during recorded questions as described above. My camera, microphone, connection, and device are working and I am ready to begin.</span>
                                    </label>
                                </div>

                                <div className="wizard-footer">
                                    <div className="wizard-footer-left">
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => setShowConfirmationStep(false)}
                                        >
                                            <ArrowLeft size={14} style={{ marginRight: 4 }} /> Back to Setup
                                        </button>
                                        {!disclaimerAccepted && (
                                            <p className="wizard-hint">{L.acceptHint}</p>
                                        )}
                                    </div>
                                    <button
                                        className="btn btn-large"
                                        onClick={handleStartInterview}
                                        disabled={startingInterview || !disclaimerAccepted}
                                    >
                                        {startingInterview ? (
                                            <>
                                                <div className="spinner-small"></div>
                                                Starting...
                                            </>
                                        ) : (
                                            L.beginButton
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Practice Step */}
                    {showPracticeStep && !interviewStarted && !successMessage && (
                        <div className="interview-setup">
                            <InterviewStepper activeIndex={2} labels={L.stepper} />
                            <div className={`setup-card${!isPracticeMode && !showPracticeReplay ? ' practice-intro-card' : ''}`}>

                                {/* Practice Replay View */}
                                {showPracticeReplay ? (
                                    <div className="practice-replay">
                                        <h2><Eye size={22} style={{ marginRight: 8 }} /> Review Your Practice Recording</h2>
                                        <p className="practice-replay-description">{L.practiceReplayBody}</p>

                                        {/* Growing row + 16:9 frame; placeholder until the blob URL is ready */}
                                        <div className="practice-replay-video-container">
                                            <div className="practice-replay-video-frame">
                                                {practiceVideoUrl ? (
                                                    <video
                                                        src={practiceVideoUrl}
                                                        controls
                                                        autoPlay
                                                        className="practice-replay-video"
                                                    />
                                                ) : (
                                                    <MediaPlaceholder
                                                        icon={<Video size={26} />}
                                                        caption="Preparing your practice replay"
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        <div className="practice-replay-checklist">
                                            <p><strong>Before continuing, confirm:</strong></p>
                                            <ul>
                                                <li><CheckCircle size={14} style={{ marginRight: 6 }} /> You can clearly see yourself in the video</li>
                                                <li><CheckCircle size={14} style={{ marginRight: 6 }} /> Your audio is clear and audible</li>
                                                <li><CheckCircle size={14} style={{ marginRight: 6 }} /> Your lighting is adequate</li>
                                                <li><CheckCircle size={14} style={{ marginRight: 6 }} /> You're comfortable with the recording setup</li>
                                            </ul>
                                        </div>

                                        <div className="practice-replay-reminder">
                                            <p><AlertTriangle size={14} style={{ marginRight: 4 }} /> Remember: each answer is one take, and question timers do not pause — stay on this page until you finish.</p>
                                        </div>
                                    </div>
                                ) : isPracticeMode ? (
                                    <div className="practice-session">
                                        {/* Video + mic column (left) — mirrors the in-session layout */}
                                        <div className="video-column">
                                            <div className="video-container">
                                                <video
                                                    ref={videoRef}
                                                    autoPlay
                                                    muted
                                                    playsInline
                                                    className={`video-preview ${isRecording ? 'recording' : ''}`}
                                                />
                                                {isRecording && (
                                                    <div className="recording-indicator">
                                                        <div className="recording-dot"></div>
                                                        RECORDING (PRACTICE)
                                                    </div>
                                                )}
                                            </div>

                                            {/* Mic Volume Bar */}
                                            <div className="audio-level-container compact">
                                                <div className="audio-level-bar-container">
                                                    <div
                                                        className={`audio-level-bar ${audioLevel > 70 ? 'level-high' : audioLevel > 30 ? 'level-mid' : 'level-low'}`}
                                                        style={{ width: `${audioLevel}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Question + live transcript column (right) */}
                                        <div className="question-active">
                                            <div className="question-header">
                                                <h2>Practice Question</h2>
                                                <div className={getTimerClassName()}>
                                                    {formatTime(timeRemaining)}
                                                </div>
                                            </div>

                                            <div className="question-main-content">
                                                <div className="question-text">
                                                    <h3>Practice Question:</h3>
                                                    <p>{practiceQuestion.text}</p>
                                                </div>

                                                <div className="transcript-container">
                                                    <h3>Live Transcript (Practice)</h3>
                                                    <div className="transcript-text">
                                                        {transcript || L.practiceTranscriptEmpty}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <h2>Practice Question</h2>
                                        <div className="practice-info">
                                            <p>{L.practiceIntroBody}</p>

                                            <div className="practice-benefits">
                                                <div className="info-item">
                                                    <span className="info-icon"><Target size={18} /></span>
                                                    <div>
                                                        <strong>Get Comfortable</strong>
                                                        <p>Practice speaking to the camera and using the interface</p>
                                                    </div>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-icon"><Settings size={18} /></span>
                                                    <div>
                                                        <strong>Test Your Setup</strong>
                                                        <p>Make sure your camera, microphone, and lighting are working well</p>
                                                    </div>
                                                </div>
                                                <div className="info-item">
                                                    <span className="info-icon"><Clock size={18} /></span>
                                                    <div>
                                                        <strong>Practice Timing</strong>
                                                        <p>Get a feel for the 30-second time limit</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="practice-note">
                                                <p><Info size={14} style={{ marginRight: 4, flexShrink: 0 }} /><span><strong>Note:</strong> This practice session won't be submitted or evaluated. After recording, you'll be able to <strong>replay the video</strong>{' '}{L.practiceNoteTail}</span></p>
                                            </div>
                                        </div>

                                        {/* Preview of the upcoming recording area, so the stage
                                            shows its own layout before anything is live */}
                                        <div className="practice-preview" aria-hidden="true">
                                            <MediaPlaceholder
                                                className="practice-preview-video"
                                                icon={<VideoOff size={22} />}
                                                caption="Your recording appears here"
                                            />
                                            <MediaPlaceholder
                                                className="practice-preview-transcript"
                                                icon={<MessageSquare size={22} />}
                                                caption="The question and your live transcript appear here while you record"
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Footer bar: secondary action bottom-left, primary bottom-right */}
                                {showPracticeReplay ? (
                                    <div className="wizard-footer">
                                        <div className="wizard-footer-left">
                                            <button
                                                className="btn btn-secondary"
                                                onClick={handleRedoPractice}
                                            >
                                                <RefreshCw size={14} style={{ marginRight: 6 }} /> Redo Practice
                                            </button>
                                        </div>
                                        <button
                                            className="btn btn-large"
                                            onClick={handleContinueAfterPractice}
                                        >
                                            <Check size={16} style={{ marginRight: 6 }} />{' '}{L.practiceContinue}
                                        </button>
                                    </div>
                                ) : isPracticeMode ? (
                                    <div className="wizard-footer">
                                        <div className="wizard-footer-left">
                                            <button
                                                className="btn btn-secondary"
                                                onClick={async () => {
                                                    console.log('🔙 Cancel practice button clicked - reloading page...');

                                                    // Stop timer
                                                    if (intervalRef.current) {
                                                        clearInterval(intervalRef.current);
                                                        intervalRef.current = null;
                                                    }

                                                    // Stop recording first (await it to ensure it completes)
                                                    try {
                                                        console.log('🛑 Stopping recording...');
                                                        await stopRecording();
                                                        console.log('✅ Recording stopped');
                                                    } catch (error) {
                                                        console.error('Error stopping recording:', error);
                                                    }

                                                    // Stop camera after recording is stopped
                                                    console.log('🛑 Stopping camera...');
                                                    stopCamera();

                                                    // Small delay to ensure cleanup completes
                                                    await new Promise(resolve => setTimeout(resolve, 100));

                                                    // Reload the page
                                                    window.location.reload();
                                                }}
                                                disabled={submittingQuestion}
                                            >
                                                Cancel & Go Back
                                            </button>
                                        </div>
                                        <button
                                            className="btn btn-large"
                                            onClick={handleCompletePractice}
                                            disabled={submittingQuestion}
                                        >
                                            Complete Practice
                                        </button>
                                    </div>
                                ) : (
                                    <div className="wizard-footer">
                                        <div className="wizard-footer-left">
                                            <button
                                                className="btn btn-secondary"
                                                onClick={async () => {
                                                    setShowPracticeStep(false);
                                                    await startActualInterview();
                                                }}
                                            >
                                                Skip Practice
                                            </button>
                                        </div>
                                        <button
                                            className="btn btn-large"
                                            onClick={handleStartPractice}
                                        >
                                            Start Practice Question
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Resume screen after an interruption — shown before the waiting screen */}
                    {interviewStarted && !successMessage && showResumeScreen && resumeContext && (
                        <InterviewResume
                            questionPosition={questionPositionOf(resumeContext.questionNumber)}
                            totalQuestions={totalQuestions}
                            answeredCount={answeredCount}
                            questionStarted={!!resumeContext.startedAt}
                            elapsedLabel={formatTime(resumeElapsedSeconds)}
                            deviceReady={mediaTestComplete}
                            resuming={startingQuestion}
                            onResume={handleResumeContinue}
                            labels={L.resume}
                            stepperLabels={L.stepper}
                        >
                            <DeviceCheckPanel
                                videoRef={videoRef}
                                mediaTestComplete={mediaTestComplete}
                                mediaTestError={mediaTestError}
                                onTest={testMediaDevices}
                                availableCameras={availableCameras}
                                availableMicrophones={availableMicrophones}
                                selectedCameraId={selectedCameraId}
                                selectedMicrophoneId={selectedMicrophoneId}
                                onCameraChange={handleCameraChange}
                                onMicrophoneChange={handleMicrophoneChange}
                                audioLevel={audioLevel}
                                labels={L.deviceCheck}
                            />
                        </InterviewResume>
                    )}

                    {/* During Interview */}
                    {interviewStarted && !successMessage && !showResumeScreen && (
                        <div className={`interview-session${atCompletionStage ? ' session-complete' : ''}`}>
                            {/* Video + Mic Column (not at the completion stage — camera is off) */}
                            {!atCompletionStage && (
                            <div className="video-column">
                                <div className="video-container">
                                    <video 
                                        ref={videoRef}
                                        autoPlay
                                        muted
                                        playsInline
                                        className={`video-preview ${isRecording ? 'recording' : ''}`}
                                        onLoadedMetadata={() => {
                                            console.log('🎥 Video metadata loaded');
                                            if (videoRef.current) {
                                                videoRef.current.play().catch(e => console.log('Autoplay prevented'));
                                            }
                                        }}
                                        onClick={() => {
                                            if (videoRef.current && videoRef.current.paused) {
                                                videoRef.current.play().catch(e => console.log('Manual play failed'));
                                            }
                                        }}
                                    />
                                    {isRecording && (
                                        <div className="recording-indicator">
                                            <div className="recording-dot"></div>
                                            RECORDING
                                        </div>
                                    )}
                                </div>

                                {/* Mic Volume Bar */}
                                <div className="audio-level-container compact">
                                    <div className="audio-level-bar-container">
                                        <div
                                            className={`audio-level-bar ${audioLevel > 70 ? 'level-high' : audioLevel > 30 ? 'level-mid' : 'level-low'}`}
                                            style={{ width: `${audioLevel}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Question Interface */}
                            <div className="question-interface">
                                {/* Inline in-session alert (warnings + blocking errors with actions) */}
                                <InterviewAlert alert={sessionAlert} onDismiss={() => setSessionAlert(null)} labels={L.alert} />

                                {/* Waiting for Question Start */}
                                {!currentQuestion && currentQuestionNumber > 0 && (
                                    <div className="question-waiting">
                                        <h2>Question {interview?.question_numbers?.indexOf(currentQuestionNumber) + 1} of {interview?.question_count}</h2>
                                        
                                        {showQuestionPreview && previewQuestionData ? (
                                            <>
                                                <div className="question-preview">
                                                    <div className="question-text">
                                                        <h3>Question:</h3>
                                                        <p>{previewQuestionData.text}</p>
                                                    </div>
                                                    {previewQuestionData.image && (
                                                        <div className="question-image-container">
                                                            <h3>Reference Image:</h3>
                                                            <div className="question-image">
                                                                <img 
                                                                    src={previewQuestionData.image} 
                                                                    alt="Question illustration"
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                    <p className="question-preview-hint">
                                                        Take your time to read the question. The timer ({previewQuestionData.time_limit} min) will start when you click the button below.
                                                    </p>
                                                </div>
                                            </>
                                        ) : showQuestionPreview && loadingPreview ? (
                                            <div className="question-preview-loading">
                                                <div className="spinner-small"></div>
                                                <p>Loading question...</p>
                                            </div>
                                        ) : (
                                            <p>Take a moment to prepare yourself. When you're ready, click the button below to start recording your response.</p>
                                        )}

                                        <div className="interview-actions">
                                            <button
                                                className="btn btn-large"
                                                onClick={() => handleStartQuestion(currentQuestionNumber)}
                                                disabled={startingQuestion || (showQuestionPreview && loadingPreview)}
                                            >
                                                {startingQuestion ? (
                                                    <>
                                                        <div className="spinner-small"></div>
                                                        Starting Question...
                                                    </>
                                                ) : showQuestionPreview && previewQuestionData ? (
                                                    "I'm Ready — Start Recording"
                                                ) : (
                                                    'Start Question'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Active Question */}
                                {currentQuestion && !isPracticeMode && (
                                    <div className="question-active">
                                        <div className="question-header">
                                            <h2>Question {interview?.question_numbers?.indexOf(currentQuestionNumber) + 1} of {interview?.question_count}</h2>
                                            <div className={getTimerClassName()}>
                                                {formatTime(timeRemaining)}
                                            </div>
                                        </div>
                                        
                                        <div className={`question-content-grid${currentQuestion.image ? ' has-image' : ''}`}>
                                            <div className="question-main-content">
                                                <div className="question-text">
                                                    <h3>Question:</h3>
                                                    <p>{currentQuestion.text || 'Question text not available'}</p>
                                                </div>
                                                
                                                <div className="transcript-container">
                                                    <h3>Live Transcript{renderTranscriptionStatus()}</h3>
                                                    <div className="transcript-text">
                                                        {transcript || 'Start speaking to see your words here...'}
                                                    </div>
                                                </div>

                                                <div className="interview-actions question-actions">
                                                    <button
                                                        className="btn btn-large"
                                                        onClick={() => handleSubmitQuestion(false)}
                                                        disabled={submittingQuestion}
                                                    >
                                                        {submittingQuestion ? (
                                                            <>
                                                                <div className="spinner-small"></div>
                                                                Submitting...
                                                            </>
                                                        ) : (
                                                            'Submit Answer'
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            {currentQuestion.image && (
                                                <div className="question-image-container">
                                                    <h3>Reference Image:</h3>
                                                    <div className="question-image">
                                                        <img 
                                                            src={currentQuestion.image} 
                                                            alt="Question illustration"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Available';
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Finalize step: no questions left, /end not yet called */}
                                {atCompletionStage && (
                                    <div className="interview-complete">
                                        <p className="complete-eyebrow">Final step</p>
                                        <h2>{(endFailed || !allQuestionsAccounted) ? L.submitTitle : L.submittingTitle}</h2>
                                        <p className="complete-summary">{answeredCount} of {totalQuestions} questions answered.</p>
                                        <p className="complete-body">
                                            {(endFailed || !allQuestionsAccounted)
                                                ? `Submitting finalizes your ${interviewType.toLowerCase()} — your answers cannot be changed after this.`
                                                : 'Your answers are being finalized automatically — please keep this page open.'}
                                        </p>
                                        {forfeitedPositions.length > 0 && (
                                            <div className="complete-forfeits">
                                                <p>
                                                    {forfeitedPositions.length === 1
                                                        ? `Question ${forfeitedPositions[0]} ran out of time and was forfeited — it will be submitted as unanswered.`
                                                        : `Questions ${forfeitedPositions.join(', ')} ran out of time and were forfeited — they will be submitted as unanswered.`}
                                                </p>
                                            </div>
                                        )}
                                        {(endFailed || !allQuestionsAccounted) ? (
                                            <div className="interview-actions">
                                                <button
                                                    className="btn btn-large"
                                                    onClick={handleEndInterview}
                                                    disabled={endingInterview}
                                                >
                                                    {endFailed ? 'Retry Submission' : L.submitButton}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="spinner" role="status" aria-label={L.submittingAria}></div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InterviewSession;
